import {
  ArrowUpRight,
  Circle,
  Copy,
  Download,
  Eraser,
  Hand,
  Loader2,
  MousePointer2,
  PenLine,
  Redo2,
  Sparkles,
  Square,
  Type,
  Undo2,
  ZoomIn,
  ZoomOut,
} from "lucide-react"
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { SketchAiFloatingCard, SketchAiFlyout } from "@/components/sketch/sketch-ai-flyout"
import { SketchArrowFlyout } from "@/components/sketch/sketch-arrow-flyout"
import {
  SketchVideoCorrectionStage,
  type SketchVideoCorrectionConfig,
} from "@/components/sketch/sketch-video-correction-stage"
import { useSketchVideoCorrection } from "@/components/sketch/use-sketch-video-correction"
import {
  flattenSketchToBlob,
  loadSketchImage,
  SketchCanvas,
  type SketchArrow,
  type SketchShape,
  type SketchStroke,
  type SketchText,
  type SketchTool,
} from "@/components/sketch/sketch-canvas"
import { EditToolbarMore } from "@/components/ui/edit-toolbar-more"
import { SingleClipPlayer } from "@/components/timeline-player"
import {
  computeEditToolbarFit,
  editOverflowSetsEqual,
  FALLBACK_EDIT_TOOLBAR_WIDTHS,
  isEditOverflowKey,
  type EditOverflowKey,
} from "@/components/sketch/sketch-edit-toolbar-fit"
import { SketchViewHeaderActions } from "@/components/sketch/sketch-view-header-actions"
import { SketchWidthFlyout } from "@/components/sketch/sketch-width-flyout"
import { PEN_PRESETS, SketchPenFlyout, type PenKind } from "@/components/sketch/sketch-pen-flyout"
import { DEFAULT_SHAPE_KIND, SketchShapeFlyout, type ShapeKind } from "@/components/sketch/sketch-shape-flyout"
import { AssetPreviewBusyDim, AssetPreviewBusySpinner } from "@/components/media/asset-preview-busy"
import { EditToolbarButton } from "@/components/ui/edit-toolbar-button"
import { FrameFormatDropdown } from "@/components/ui/frame-format-select"
import { Button } from "@/components/ui/primitives"
import { PromptEditor, type PromptEditorHandle } from "@/components/prompt-editor/prompt-editor"
import type { MentionItem } from "@/components/prompt-editor/types"
import { ReferenceStrip } from "@/components/references/reference-strip"
import { formatTimecode } from "@/components/timeline-player/timeline-chrome"
import { WorkspaceDialog } from "@/components/workspace/workspace-dialog"
import { useWorkspaceEntitySplitDesktop } from "@/components/workspace/workspace-entity-split-layout"
import { useTranslation } from "@/hooks/use-translation"
import { confirm } from "@/lib/confirm"
import { cancelConfirmDelay, confirmDelayKey } from "@/lib/confirm-delay-registry"
import { copyImageToClipboard, downloadImage } from "@/lib/download-image"
import { ensureMinImageBlob } from "@/lib/ensure-min-image-blob"
import type { LibrarySketchItem } from "@/lib/library-api"
import type { TimelineDocument } from "@/lib/timeline"
import { FRAME_FORMATS, frameFormatRatio, normalizeFrameFormat, type FrameFormat } from "@/lib/frame-format"
import { DEFAULT_VIDEO_OUTPUT_FPS } from "@/lib/video-output-fps"
import { fetchSketchEdit, postSketchEdit, promoteSketchEditToLibrary } from "@/lib/sketch-api"
import type { CanvasGenerationRow } from "@/types/canvas"
import { clearSketchEditWaiter, getSketchEditWaiter, setSketchEditWaiter } from "@/lib/sketch-edit-waiter"
import {
  cameraForPreset,
  displayZoomPercent,
  FIT_CAMERA,
  matchingZoomPreset,
  SKETCH_VIEWPORT_MAX_ZOOM,
  SKETCH_VIEWPORT_MIN_ZOOM,
  SKETCH_ZOOM_STEP,
  sourceOneToOneZoom,
  zoomAt,
  type SketchCamera,
  type SketchZoomPreset,
} from "@/lib/sketch-viewport"
import { cn } from "@/lib/utils"
import { useWorkspaceJobsStore } from "@/stores/workspace-jobs-store"

const DEFAULT_PEN: PenKind = "marker"
const DEFAULT_COLOR = PEN_PRESETS[DEFAULT_PEN].color
const DEFAULT_ERASER_WIDTH = 24
const DEFAULT_ARROW_WIDTH = 4
const DEFAULT_SHAPE_WIDTH = 2
const HISTORY_LIMIT = 50
const SKETCH_SEQ_KEY = "sketch-seq"
const VIEW_HEADER_PAD_X = 24
const VIEW_CLOSE_RESERVE = 44
const VIEW_TITLE_RESERVE = 72
const VIEW_ACTIONS_GAP = 8
/** Right column (prompt + markups + Start) in video-correction mode. */
const VIDEO_SIDEBAR_W = 320
const VIDEO_SIDEBAR_GAP = 12
const CHROME_HEADER_H = 56
const EDIT_BODY_PAD_Y = 24
const EDIT_STAGE_PAD_Y = 24
const EDIT_FOOTER_H = 72

function measuredOuterWidth(el: Element | null, fallback: number): number {
  if (!(el instanceof HTMLElement)) return fallback
  const style = getComputedStyle(el)
  return el.offsetWidth + parseFloat(style.marginLeft) + parseFloat(style.marginRight)
}

function nearestFrameFormat(ratio: number): FrameFormat {
  let best: FrameFormat = "16:9"
  let bestDiff = Number.POSITIVE_INFINITY
  for (const format of FRAME_FORMATS) {
    const diff = Math.abs(frameFormatRatio(format) - ratio)
    if (diff < bestDiff) {
      bestDiff = diff
      best = format
    }
  }
  return best
}

function aspectRatioForLayout(
  layoutKey: LayoutKey,
  width: number,
  height: number,
  sourceAspect: number | null,
): string {
  if (layoutKey === "source") {
    if (sourceAspect != null && sourceAspect > 0) return nearestFrameFormat(sourceAspect)
    if (width > 0 && height > 0) return nearestFrameFormat(width / height)
    return "16:9"
  }
  if (layoutKey === "fill") {
    if (width <= 0 || height <= 0) return "16:9"
    return nearestFrameFormat(width / height)
  }
  return layoutKey
}

type TipSettings = { color: string; width: number; opacity: number }

function defaultTipSettings(): Record<PenKind, TipSettings> {
  return {
    marker: {
      color: PEN_PRESETS.marker.color,
      width: PEN_PRESETS.marker.width,
      opacity: PEN_PRESETS.marker.opacity,
    },
    highlighter: {
      color: PEN_PRESETS.highlighter.color,
      width: PEN_PRESETS.highlighter.width,
      opacity: PEN_PRESETS.highlighter.opacity,
    },
  }
}

function nextSketchNumber(): number {
  try {
    const next = Number(sessionStorage.getItem(SKETCH_SEQ_KEY) || "0") + 1
    sessionStorage.setItem(SKETCH_SEQ_KEY, String(next))
    return next
  } catch {
    return Date.now() % 100000
  }
}

function toSketchFileName(raw: string, fallback: string): string {
  const cleaned = raw
    .trim()
    .replace(/[/\\?%*:|"<>]/g, "-")
    .replace(/\s+/g, " ")
    .replace(/\.png$/i, "")
  const base = cleaned || fallback.replace(/\.png$/i, "")
  return `${base}.png`
}

export type SketchAvailability = {
  available: boolean
  modelLabel: string
  modelKey?: string
  disabledReason?: string
}

export type SketchCharacterTarget = "portrait" | "reference" | "emotion" | "outfit"

export type SketchReplaceCharacterOnCorrect = {
  characterId: number
  target: SketchCharacterTarget
  view?: string
  sheetScope?: "full_body" | "headless"
  outfitId?: number
}

export type SketchPropTarget = "front_view" | "reference"

export type SketchReplacePropOnCorrect = {
  propId: number
  target: SketchPropTarget
  stateId?: number
}

export type SketchReplaceLocationOnCorrect = {
  locationId: number
  variantId?: number
  facing: string
}

export type SketchCorrectionPendingChange = {
  jobId: number
  active: boolean
}

/** Lightbox vs canvas chrome. Header Edit switches view → edit. Same dialog, not a second modal. */
export type SketchPresentation = "view" | "edit"

/** Footer buttons. Callbacks say what to do; this says whether to show them. */
export type SketchPersist = "none" | "confirm" | "library" | "confirmAndLibrary"

export type { SketchVideoCorrectionConfig } from "@/components/sketch/sketch-video-correction-stage"

export type SketchDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
  sourceUrl?: string | null
  /**
   * View-only video stage (e.g. location previz). Skips image canvas / Edit entry.
   * Prefer with `presentation="view"` and `allowEdit={false}`.
   */
  videoUrl?: string | null
  /**
   * Previz view: trim clip edges, then save a new previz. Other video views stay read-only.
   * Dialog closes after the promise resolves. A rejection keeps it open.
   */
  onSaveVideoCut?: (range: { sourceInMs: number; sourceOutMs: number }) => Promise<void>
  /** Known file length for previz trim. Without it the player learns duration from the file. */
  videoDurationMs?: number | null
  /**
   * View-only audio stage. Skips image canvas / Edit entry.
   * Prefer with `presentation="view"` and `allowEdit={false}`.
   */
  audioUrl?: string | null
  projectFrameFormat?: string | null
  sketchEdit?: SketchAvailability | null
  /** Start in lightbox (`view`) or markup canvas (`edit`). Default `edit`. */
  presentation?: SketchPresentation
  /** When false, view chrome cannot switch to markup. Default true. */
  allowEdit?: boolean
  /** Bottom persist actions. Default `none` (no footer). */
  persist?: SketchPersist
  /** Primary footer says Save instead of Use as reference. */
  confirmAsSave?: boolean
  /** Override primary footer label (e.g. Replace portrait). */
  confirmLabel?: string
  /** Prefill dialog title / export filename (e.g. reopen from jobs panel). */
  initialFileName?: string | null
  /** UI header only. Does not change export filename or job sketch_file_name. */
  headerTitle?: string | null
  onConfirm?: (file: File) => void | Promise<void>
  /** Reuse AI→library upload when canvas was not marked up after that result (strip path). */
  onConfirmExistingLibraryRef?: (ref: LibrarySketchItem) => void | Promise<void>
  onSaveToLibrary?: (file: File) => void | Promise<void>
  /** Clean AI result already in library — save-to-library without re-upload. */
  onSaveExistingToLibrary?: (ref: LibrarySketchItem) => void | Promise<void>
  onLibrarySaved?: () => void | Promise<void>
  onCorrectionPendingChange?: (state: SketchCorrectionPendingChange) => void
  /** Shot-panel: enqueue replaces the frame; dialog closes like CorrectionPromptDialog. */
  replaceShotOnCorrect?: { shotId: number; tab: "storyboard" | "shooting_board" }
  /** Character slot: enqueue writes a new entity version; dialog closes like shot markup. */
  replaceCharacterOnCorrect?: SketchReplaceCharacterOnCorrect
  /** Prop slot: enqueue writes a new entity version; dialog closes like shot markup. */
  replacePropOnCorrect?: SketchReplacePropOnCorrect
  /** Location slot: enqueue writes a new entity version; dialog closes like shot markup. */
  replaceLocationOnCorrect?: SketchReplaceLocationOnCorrect
  /** Canvas edit: enqueue queued tile, close dialog, do not wait in sketch. */
  replaceCanvasOnCorrect?: { canvasId: number; sourceGenerationId: number }
  onCanvasCorrectionQueued?: (generation: CanvasGenerationRow, clientId: number) => void
  /** Canvas: prepend optimistic queued tile before background enqueue. */
  onCanvasCorrectionOptimistic?: (sourceGenerationId: number, clientId: number) => void
  /** Canvas: remove optimistic tile when background enqueue fails. */
  onCanvasCorrectionEnqueueFailed?: (clientId: number) => void
  /** Jobs reopen: allow Save with empty canvas (AI result is the deliverable). */
  allowCleanLibrarySave?: boolean
  /** Raise above fullscreen hosts like ShotPanel (z 100/101). */
  nested?: boolean | 2
  /**
   * AI correction panel (floating card / mobile flyout). Default true.
   * Annotate-only hosts (e.g. video correction markup) pass false.
   */
  showAiPanel?: boolean
  /**
   * Stable SPA reconnect id for AI Apply.
   * Default: `defaultSketchAiConfirmDelayKey` from replace* / source pathname / file name.
   */
  confirmDelayKey?: string
  /**
   * Optional video correction mode: same SketchDialog chrome + TimelinePlayer stage.
   * Forces AI panel off and disables canvas zoom.
   */
  videoCorrection?: SketchVideoCorrectionConfig | null
}

type LayoutKey = "fill" | "source" | FrameFormat

type Snapshot = {
  baseUrl: string | null
  strokes: SketchStroke[]
  texts: SketchText[]
  arrows: SketchArrow[]
  shapes: SketchShape[]
}

/** Per-frame ink for video-correction markups (no baseUrl). */
type FrameInkSnapshot = {
  strokes: SketchStroke[]
  texts: SketchText[]
  arrows: SketchArrow[]
  shapes: SketchShape[]
}

function snapInkMs(ms: number, fps: number): number {
  const safeFps = fps > 0 ? fps : DEFAULT_VIDEO_OUTPUT_FPS
  const frameMs = 1000 / safeFps
  return Math.round(Math.round(ms / frameMs) * frameMs)
}

function cloneFrameInk(snap: FrameInkSnapshot): FrameInkSnapshot {
  return structuredClone(snap)
}

function emptyFrameInk(): FrameInkSnapshot {
  return { strokes: [], texts: [], arrows: [], shapes: [] }
}

function fitRatioSize(paneW: number, paneH: number, ratio: number): { width: number; height: number } {
  if (paneW <= 0 || paneH <= 0 || ratio <= 0) return { width: 16, height: 16 }
  if (paneW / paneH >= ratio) {
    const height = Math.max(16, Math.floor(paneH))
    const width = Math.max(16, Math.floor(height * ratio))
    return { width: Math.min(width, paneW), height }
  }
  const width = Math.max(16, Math.floor(paneW))
  const height = Math.max(16, Math.floor(width / ratio))
  return { width, height: Math.min(height, paneH) }
}

function fitFormatSize(paneW: number, paneH: number, format: FrameFormat): { width: number; height: number } {
  return fitRatioSize(paneW, paneH, frameFormatRatio(format))
}

function scaleStrokes(
  strokes: SketchStroke[],
  mapX: (x: number) => number,
  mapY: (y: number) => number,
  brushScale: number,
): SketchStroke[] {
  if (brushScale === 1 && strokes.length === 0) return strokes
  return strokes.map((stroke) => ({
    ...stroke,
    width: Math.max(1, stroke.width * brushScale),
    points: stroke.points.map((p) => ({ x: mapX(p.x), y: mapY(p.y) })),
  }))
}

function scaleTexts(
  texts: SketchText[],
  mapX: (x: number) => number,
  mapY: (y: number) => number,
  brushScale: number,
): SketchText[] {
  return texts.map((t) => ({
    ...t,
    x: mapX(t.x),
    y: mapY(t.y),
    fontSize: Math.max(8, Math.round(t.fontSize * brushScale)),
  }))
}

function scaleArrows(
  arrows: SketchArrow[],
  mapX: (x: number) => number,
  mapY: (y: number) => number,
  brushScale: number,
): SketchArrow[] {
  return arrows.map((a) => ({
    ...a,
    from: { x: mapX(a.from.x), y: mapY(a.from.y) },
    to: { x: mapX(a.to.x), y: mapY(a.to.y) },
    width: Math.max(1, a.width * brushScale),
    labelFontSize: Math.max(8, Math.round((a.labelFontSize ?? 16) * brushScale)),
  }))
}

function scaleShapes(
  shapes: SketchShape[],
  mapX: (x: number) => number,
  mapY: (y: number) => number,
  brushScale: number,
): SketchShape[] {
  return shapes.map((s) => {
    const x2 = mapX(s.x + s.w)
    const y2 = mapY(s.y + s.h)
    const x1 = mapX(s.x)
    const y1 = mapY(s.y)
    return {
      ...s,
      x: x1,
      y: y1,
      w: Math.max(1, x2 - x1),
      h: Math.max(1, y2 - y1),
      width: Math.max(1, s.width * brushScale),
    }
  })
}

const EXPORT_MAX_SIDE = 4096

type ExportTransform = {
  width: number
  height: number
  mapX: (x: number) => number
  mapY: (y: number) => number
  brushScale: number
  imageFit: "contain" | "stretch"
}

/** Display (contain-fitted preview) → natural export canvas without letterbox bars. */
function buildExportTransform(
  display: { width: number; height: number },
  natural: { width: number; height: number } | null,
  hasBase: boolean,
): ExportTransform {
  const identity = {
    width: Math.max(1, display.width),
    height: Math.max(1, display.height),
    mapX: (x: number) => x,
    mapY: (y: number) => y,
    brushScale: 1,
    imageFit: "contain" as const,
  }
  if (
    !hasBase ||
    natural == null ||
    natural.width < 1 ||
    natural.height < 1 ||
    display.width < 1 ||
    display.height < 1
  ) {
    return identity
  }
  const fitScale = Math.min(display.width / natural.width, display.height / natural.height)
  if (!(fitScale > 0) || !Number.isFinite(fitScale)) return identity

  const ox = (display.width - natural.width * fitScale) / 2
  const oy = (display.height - natural.height * fitScale) / 2
  let exportW = natural.width
  let exportH = natural.height
  let down = 1
  const longSide = Math.max(exportW, exportH)
  if (longSide > EXPORT_MAX_SIDE) {
    down = EXPORT_MAX_SIDE / longSide
    exportW = Math.max(1, Math.round(exportW * down))
    exportH = Math.max(1, Math.round(exportH * down))
  }

  return {
    width: exportW,
    height: exportH,
    mapX: (x) => ((x - ox) / fitScale) * down,
    mapY: (y) => ((y - oy) / fitScale) * down,
    brushScale: down / fitScale,
    imageFit: "stretch",
  }
}

/** Pathname only — signed query must not churn the reconnect id. */
function sketchMediaPathScope(url: string | null | undefined): string | null {
  const raw = url?.trim()
  if (!raw) return null
  try {
    return new URL(raw, typeof window !== "undefined" ? window.location.origin : "http://localhost").pathname
  } catch {
    return raw.slice(0, 160)
  }
}

type SketchAiConfirmDelayArgs = {
  projectId: number
  sourceUrl?: string | null
  videoUrl?: string | null
  audioUrl?: string | null
  initialFileName?: string | null
  replaceShotOnCorrect?: SketchDialogProps["replaceShotOnCorrect"]
  replaceCharacterOnCorrect?: SketchReplaceCharacterOnCorrect
  replacePropOnCorrect?: SketchReplacePropOnCorrect
  replaceLocationOnCorrect?: SketchReplaceLocationOnCorrect
  replaceCanvasOnCorrect?: SketchDialogProps["replaceCanvasOnCorrect"]
}

/** Default SPA reconnect id for Sketch AI Apply (override via `confirmDelayKey` prop). */
export function defaultSketchAiConfirmDelayKey({
  projectId,
  sourceUrl,
  videoUrl,
  audioUrl,
  initialFileName,
  replaceShotOnCorrect,
  replaceCharacterOnCorrect,
  replacePropOnCorrect,
  replaceLocationOnCorrect,
  replaceCanvasOnCorrect,
}: SketchAiConfirmDelayArgs): string {
  const scope: Array<string | number> = ["sketch", projectId]

  if (replaceShotOnCorrect) {
    scope.push("shot", replaceShotOnCorrect.shotId, replaceShotOnCorrect.tab)
  } else if (replaceCharacterOnCorrect) {
    scope.push("character", replaceCharacterOnCorrect.characterId, replaceCharacterOnCorrect.target)
    if (replaceCharacterOnCorrect.view) scope.push(replaceCharacterOnCorrect.view)
    if (replaceCharacterOnCorrect.sheetScope) scope.push(replaceCharacterOnCorrect.sheetScope)
    if (replaceCharacterOnCorrect.outfitId != null) scope.push(replaceCharacterOnCorrect.outfitId)
  } else if (replacePropOnCorrect) {
    scope.push("prop", replacePropOnCorrect.propId, replacePropOnCorrect.target)
    if (replacePropOnCorrect.stateId != null) scope.push(replacePropOnCorrect.stateId)
  } else if (replaceLocationOnCorrect) {
    scope.push("location", replaceLocationOnCorrect.locationId)
    if (replaceLocationOnCorrect.variantId != null) scope.push("variant", replaceLocationOnCorrect.variantId)
    scope.push(replaceLocationOnCorrect.facing)
  } else if (replaceCanvasOnCorrect) {
    scope.push("canvas", replaceCanvasOnCorrect.canvasId, replaceCanvasOnCorrect.sourceGenerationId)
  } else {
    const path = sketchMediaPathScope(sourceUrl) ?? sketchMediaPathScope(videoUrl) ?? sketchMediaPathScope(audioUrl)
    if (path) scope.push("src", path)
    else if (initialFileName?.trim()) scope.push("file", initialFileName.trim())
    else scope.push("blank")
  }

  scope.push("ai")
  return confirmDelayKey(...scope)
}

export function SketchDialog(props: SketchDialogProps) {
  return <SketchDialogInner {...props} />
}

function SketchDialogInner({
  open,
  onOpenChange,
  projectId,
  sourceUrl = null,
  videoUrl = null,
  onSaveVideoCut,
  videoDurationMs = null,
  audioUrl = null,
  projectFrameFormat = null,
  sketchEdit = null,
  presentation = "edit",
  allowEdit = true,
  persist = "none",
  confirmAsSave = false,
  confirmLabel,
  initialFileName = null,
  headerTitle = null,
  onConfirm,
  onConfirmExistingLibraryRef,
  onSaveToLibrary,
  onSaveExistingToLibrary,
  onLibrarySaved,
  onCorrectionPendingChange,
  replaceShotOnCorrect,
  replaceCharacterOnCorrect,
  replacePropOnCorrect,
  replaceLocationOnCorrect,
  replaceCanvasOnCorrect,
  onCanvasCorrectionQueued,
  onCanvasCorrectionOptimistic,
  onCanvasCorrectionEnqueueFailed,
  allowCleanLibrarySave = false,
  nested = false,
  showAiPanel = true,
  confirmDelayKey: confirmDelayKeyProp,
  videoCorrection = null,
}: SketchDialogProps) {
  const { t } = useTranslation(["workspace", "common", "shot"])
  const isDesktop = useWorkspaceEntitySplitDesktop()
  const videoMode = Boolean(videoCorrection)
  const [videoCut, setVideoCut] = useState<{ inMs: number; outMs: number } | null>(null)
  const [videoFileDurationMs, setVideoFileDurationMs] = useState(0)
  const [videoCutSaving, setVideoCutSaving] = useState(false)
  const videoViewMode = Boolean(videoUrl?.trim()) && !videoMode
  const audioViewMode = Boolean(audioUrl?.trim()) && !videoMode && !videoViewMode
  const mediaViewMode = videoViewMode || audioViewMode
  const effectiveShowAiPanel = showAiPanel && !videoMode && !mediaViewMode
  const resolvedAiConfirmDelayKey = useMemo(
    () =>
      confirmDelayKeyProp ??
      defaultSketchAiConfirmDelayKey({
        projectId,
        sourceUrl,
        videoUrl,
        audioUrl,
        initialFileName,
        replaceShotOnCorrect,
        replaceCharacterOnCorrect,
        replacePropOnCorrect,
        replaceLocationOnCorrect,
        replaceCanvasOnCorrect,
      }),
    [
      confirmDelayKeyProp,
      projectId,
      sourceUrl,
      videoUrl,
      audioUrl,
      initialFileName,
      replaceShotOnCorrect,
      replaceCharacterOnCorrect,
      replacePropOnCorrect,
      replaceLocationOnCorrect,
      replaceCanvasOnCorrect,
    ],
  )

  // Close dialog → drop AI Apply countdown (do not cancel on unmount while open).
  useEffect(() => {
    if (!open) cancelConfirmDelay(resolvedAiConfirmDelayKey)
  }, [open, resolvedAiConfirmDelayKey])

  const paneRef = useRef<HTMLDivElement>(null)
  const viewAudioRef = useRef<HTMLAudioElement>(null)
  const penButtonRef = useRef<HTMLButtonElement>(null)
  const eraserButtonRef = useRef<HTMLButtonElement>(null)
  const shapeButtonRef = useRef<HTMLButtonElement>(null)
  const arrowButtonRef = useRef<HTMLButtonElement>(null)
  const arrowLabelButtonRef = useRef<HTMLButtonElement>(null)
  const aiButtonRef = useRef<HTMLButtonElement>(null)
  const moreButtonRef = useRef<HTMLButtonElement>(null)
  const toolbarRootRef = useRef<HTMLDivElement>(null)
  const toolbarMeasureRef = useRef<HTMLDivElement>(null)
  const sizeRef = useRef({ width: 0, height: 0 })
  const skipHistoryRef = useRef(false)
  const [paneSize, setPaneSize] = useState({ width: 0, height: 0 })
  const [layoutKey, setLayoutKey] = useState<LayoutKey>(() =>
    sourceUrl ? "source" : normalizeFrameFormat(projectFrameFormat),
  )
  const [sourceAspect, setSourceAspect] = useState<number | null>(null)
  const [sourceNatural, setSourceNatural] = useState<{ width: number; height: number } | null>(null)
  const [baseUrl, setBaseUrl] = useState<string | null>(sourceUrl)
  const [baseImage, setBaseImage] = useState<HTMLImageElement | null>(null)
  const baseLoadGenRef = useRef(0)
  const [strokes, setStrokes] = useState<SketchStroke[]>([])
  const [texts, setTexts] = useState<SketchText[]>([])
  const [arrows, setArrows] = useState<SketchArrow[]>([])
  const [shapes, setShapes] = useState<SketchShape[]>([])
  const [history, setHistory] = useState<Snapshot[]>([])
  const [future, setFuture] = useState<Snapshot[]>([])
  const [tool, setTool] = useState<SketchTool>("pen")
  const [penFlyoutOpen, setPenFlyoutOpen] = useState(false)
  const [eraserFlyoutOpen, setEraserFlyoutOpen] = useState(false)
  const [shapeFlyoutOpen, setShapeFlyoutOpen] = useState(false)
  const [arrowFlyoutOpen, setArrowFlyoutOpen] = useState(false)
  const [aiFlyoutOpen, setAiFlyoutOpen] = useState(false)
  const [moreMenuOpen, setMoreMenuOpen] = useState(false)
  const [editOverflowHidden, setEditOverflowHidden] = useState<Set<EditOverflowKey>>(() => new Set())
  const [editShowResolution, setEditShowResolution] = useState(true)
  const [editShowFormat, setEditShowFormat] = useState(true)
  const [penKind, setPenKind] = useState<PenKind>(DEFAULT_PEN)
  const [tipSettings, setTipSettings] = useState<Record<PenKind, TipSettings>>(defaultTipSettings)
  const [eraserWidth, setEraserWidth] = useState<number>(DEFAULT_ERASER_WIDTH)
  const [shapeKind, setShapeKind] = useState<ShapeKind>(DEFAULT_SHAPE_KIND)
  const [shapeColor, setShapeColor] = useState<string>(DEFAULT_COLOR)
  const [shapeWidth, setShapeWidth] = useState<number>(DEFAULT_SHAPE_WIDTH)
  const [arrowColor, setArrowColor] = useState<string>(DEFAULT_COLOR)
  const [arrowWidth, setArrowWidth] = useState<number>(DEFAULT_ARROW_WIDTH)
  const [arrowLabelColor, setArrowLabelColor] = useState<string>(DEFAULT_COLOR)
  const [arrowLabelWidth, setArrowLabelWidth] = useState<number>(DEFAULT_ARROW_WIDTH)
  const [textColor, setTextColor] = useState<string>(DEFAULT_COLOR)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [editingTextId, setEditingTextId] = useState<string | null>(null)
  const [editingArrowId, setEditingArrowId] = useState<string | null>(null)
  const [prompt, setPrompt] = useState("")
  const [aiBusy, setAiBusy] = useState(false)
  const [pendingJobId, setPendingJobId] = useState<number | null>(null)
  const [pendingEditId, setPendingEditId] = useState<number | null>(null)
  const [completedEditId, setCompletedEditId] = useState<number | null>(null)
  const [confirmBusy, setConfirmBusy] = useState(false)
  const [fileName, setFileName] = useState("")
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState("")
  const [aiLibraryRef, setAiLibraryRef] = useState<LibrarySketchItem | null>(null)
  const [aiSourceUrl, setAiSourceUrl] = useState<string | null>(null)
  const titleInputRef = useRef<HTMLInputElement>(null)
  const aiSessionRef = useRef(0)
  const finishingRef = useRef(false)
  const onCorrectionPendingChangeRef = useRef(onCorrectionPendingChange)
  onCorrectionPendingChangeRef.current = onCorrectionPendingChange
  const ownerId = useId()
  const [viewMode, setViewMode] = useState(presentation === "view" || !allowEdit)
  const [copying, setCopying] = useState(false)
  const [camera, setCamera] = useState<SketchCamera>(FIT_CAMERA)
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 1280,
    h: typeof window !== "undefined" ? window.innerHeight : 720,
  }))

  const videoInkFps =
    videoCorrection?.frameRate != null && videoCorrection.frameRate > 0
      ? videoCorrection.frameRate
      : DEFAULT_VIDEO_OUTPUT_FPS

  const frameInkByMsRef = useRef(new Map<number, FrameInkSnapshot>())
  const inkSettledMsRef = useRef<number | null>(null)
  const inkLiveRef = useRef<FrameInkSnapshot>(emptyFrameInk())
  inkLiveRef.current = { strokes, texts, arrows, shapes }

  const applyFrameInk = useCallback((snap: FrameInkSnapshot | null) => {
    const next = snap ?? emptyFrameInk()
    setStrokes(next.strokes)
    setTexts(next.texts)
    setArrows(next.arrows)
    setShapes(next.shapes)
    setHistory([])
    setFuture([])
    setSelectedIds([])
    setEditingTextId(null)
    setEditingArrowId(null)
  }, [])

  const restoreInkForFrame = useCallback(
    (nextMs: number) => {
      const nextKey = snapInkMs(nextMs, videoInkFps)
      inkSettledMsRef.current = nextKey
      applyFrameInk(frameInkByMsRef.current.get(nextKey) ?? null)
    },
    [videoInkFps, applyFrameInk],
  )

  /** Timeline scrub/play started — hide live ink immediately; Map keeps only saved markups. */
  const dismissLiveInk = useCallback(() => {
    applyFrameInk(null)
  }, [applyFrameInk])

  const rememberFrameInk = useCallback(
    (timecodeMs: number) => {
      const key = snapInkMs(timecodeMs, videoInkFps)
      frameInkByMsRef.current.set(key, cloneFrameInk(inkLiveRef.current))
      inkSettledMsRef.current = key
    },
    [videoInkFps],
  )

  useEffect(() => {
    if (open && videoMode) return
    frameInkByMsRef.current.clear()
    inkSettledMsRef.current = null
  }, [open, videoMode])

  const videoStageSizeRef = useRef({ width: 640, height: 360 })
  const videoPromptEditorRef = useRef<PromptEditorHandle>(null)

  const buildVideoMarkupBlob = useCallback(
    async (frameImage: HTMLImageElement) => {
      const display = videoStageSizeRef.current
      const natural = {
        width: frameImage.naturalWidth,
        height: frameImage.naturalHeight,
      }
      const xf = buildExportTransform(display, natural, true)
      const blob = await flattenSketchToBlob({
        width: xf.width,
        height: xf.height,
        baseImage: frameImage,
        background: "none",
        imageFit: xf.imageFit,
        strokes: scaleStrokes(strokes, xf.mapX, xf.mapY, xf.brushScale),
        texts: scaleTexts(texts, xf.mapX, xf.mapY, xf.brushScale),
        arrows: scaleArrows(arrows, xf.mapX, xf.mapY, xf.brushScale),
        shapes: scaleShapes(shapes, xf.mapX, xf.mapY, xf.brushScale),
      })
      // BytePlus Asset CreateAsset rejects images under 300px (failover after real-person).
      return ensureMinImageBlob(blob)
    },
    [strokes, texts, arrows, shapes],
  )

  const video = useSketchVideoCorrection({
    open: open && videoMode,
    projectId,
    config: videoCorrection,
    onOpenChange,
    startFailedMessage: t("correction.startFailed", { ns: "shot" }),
    captureUnavailableMessage: t("correction.captureUnavailable", { ns: "shot" }),
    buildMarkupBlob: buildVideoMarkupBlob,
    onPlayheadSettled: restoreInkForFrame,
    onMarkupSaved: rememberFrameInk,
  })
  videoStageSizeRef.current = video.stageSize

  const videoFps =
    videoCorrection?.frameRate != null && videoCorrection.frameRate > 0
      ? videoCorrection.frameRate
      : DEFAULT_VIDEO_OUTPUT_FPS

  const videoMarkupMentionItems = useMemo<MentionItem[]>(
    () =>
      video.markups.map((m) => {
        const tc = formatTimecode(m.timecodeMs, videoFps)
        return {
          category: "reference" as const,
          id: m.mentionIndex,
          label: `@${tc}`,
          imageUrl: m.previewUrl,
        }
      }),
    [video.markups, videoFps],
  )

  const handleSaveVideoMarkup = useCallback(async () => {
    const added = await video.addMarkup()
    if (!added) return
    const label = `@${formatTimecode(added.timecodeMs, videoFps)}`
    // Mention list updates after render; insert on next tick so chip resolves.
    window.setTimeout(() => {
      videoPromptEditorRef.current?.insertReference(label)
    }, 0)
  }, [video, videoFps])

  const penColor = tipSettings[penKind].color
  const brushWidth = tipSettings[penKind].width
  const brushOpacity = tipSettings[penKind].opacity

  const patchTipSettings = useCallback(
    (partial: Partial<TipSettings>) => {
      setTipSettings((prev) => ({
        ...prev,
        [penKind]: { ...prev[penKind], ...partial },
      }))
    },
    [penKind],
  )

  const showFooter = videoMode || (!viewMode && persist !== "none")
  const showLibraryPersist = !videoMode && (persist === "library" || persist === "confirmAndLibrary")
  const showConfirmPersist = !videoMode && (persist === "confirm" || persist === "confirmAndLibrary")
  const chromeTitle = headerTitle?.trim() || null
  const headerLabel =
    chromeTitle ||
    (videoMode ? t("correction.videoTitle", { ns: "shot" }) : null) ||
    fileName ||
    t("canvas.sketch.title")
  const isNewSketch = !sourceUrl && !videoMode && !mediaViewMode
  const flushEditWell = !viewMode && !isNewSketch && !videoMode
  const lockHeaderTitle = Boolean(chromeTitle) || videoMode

  const previewMaxW = Math.floor(Math.min(viewport.w * 0.96, 1280))
  const viewMaxH = Math.max(16, Math.floor(Math.min(viewport.h * 0.82, viewport.h * 0.95 - CHROME_HEADER_H)))
  const editBodyPadY = isNewSketch ? EDIT_BODY_PAD_Y : 0
  const editStagePadY = isNewSketch ? EDIT_STAGE_PAD_Y : 0
  const editMaxH = Math.max(
    16,
    Math.min(
      viewMaxH,
      Math.floor(viewport.h * 0.95 - CHROME_HEADER_H - editBodyPadY - editStagePadY - (showFooter ? EDIT_FOOTER_H : 0)),
    ),
  )

  const previewAspect =
    sourceAspect != null && sourceAspect > 0
      ? sourceAspect
      : sourceNatural && sourceNatural.height > 0
        ? sourceNatural.width / sourceNatural.height
        : null
  const viewSizeReady = previewAspect != null && previewAspect > 0

  const viewPreviewSize = useMemo(() => {
    const aspect =
      viewSizeReady && previewAspect != null
        ? previewAspect
        : frameFormatRatio(normalizeFrameFormat(projectFrameFormat))
    return fitRatioSize(previewMaxW, viewMaxH, aspect)
  }, [previewAspect, previewMaxW, viewMaxH, viewSizeReady, projectFrameFormat])

  const editPreviewSize = useMemo(() => {
    if (layoutKey === "fill") {
      return { width: Math.max(16, previewMaxW), height: Math.max(16, editMaxH) }
    }
    if (layoutKey === "source" && sourceAspect != null && sourceAspect > 0) {
      return fitRatioSize(previewMaxW, editMaxH, sourceAspect)
    }
    if (layoutKey === "source") {
      return fitFormatSize(previewMaxW, editMaxH, normalizeFrameFormat(projectFrameFormat))
    }
    return fitFormatSize(previewMaxW, editMaxH, layoutKey)
  }, [layoutKey, sourceAspect, projectFrameFormat, previewMaxW, editMaxH])

  const size = useMemo(() => {
    if (!isNewSketch) return editPreviewSize
    const paneW = paneSize.width > 0 ? paneSize.width : previewMaxW
    const paneH = paneSize.height > 0 ? paneSize.height : editMaxH
    if (layoutKey === "source" && sourceAspect != null && sourceAspect > 0) {
      return fitRatioSize(paneW, editMaxH, sourceAspect)
    }
    if (layoutKey === "fill") {
      return { width: Math.max(16, Math.floor(paneW)), height: Math.max(16, Math.floor(paneH)) }
    }
    if (layoutKey === "source") {
      return fitFormatSize(paneW, paneH, normalizeFrameFormat(projectFrameFormat))
    }
    return fitFormatSize(paneW, paneH, layoutKey)
  }, [isNewSketch, editPreviewSize, paneSize, layoutKey, sourceAspect, projectFrameFormat, previewMaxW, editMaxH])

  // Match TimelinePlayer shell max-h so dialog width keeps contain without side letterbox.
  const videoShellMaxH = Math.floor(Math.min(viewport.h * 0.6, 520))
  const videoBodyChromeH = CHROME_HEADER_H + EDIT_FOOTER_H + EDIT_BODY_PAD_Y + 120
  const videoMaxH = Math.max(16, Math.min(videoShellMaxH, Math.floor(viewport.h * 0.95 - videoBodyChromeH)))
  const videoAspect =
    videoCorrection?.aspectRatio != null && videoCorrection.aspectRatio > 0
      ? videoCorrection.aspectRatio
      : sourceAspect != null && sourceAspect > 0
        ? sourceAspect
        : 16 / 9
  const videoViewWidth = useMemo(() => {
    const stage = fitRatioSize(previewMaxW, videoShellMaxH, videoAspect)
    return Math.min(stage.width, Math.floor(viewport.w * 0.96), 1280)
  }, [previewMaxW, videoShellMaxH, videoAspect, viewport.w])

  const viewDialogWidth = useMemo(() => {
    if (audioViewMode) {
      return Math.min(Math.floor(viewport.w * 0.96), 576)
    }
    if (videoViewMode) return videoViewWidth
    return Math.min(viewPreviewSize.width, Math.floor(viewport.w * 0.96), 1280)
  }, [audioViewMode, videoViewMode, videoViewWidth, viewPreviewSize.width, viewport.w])

  const editDialogWidth = useMemo(() => {
    return Math.min(editPreviewSize.width, Math.floor(viewport.w * 0.96), 1280)
  }, [editPreviewSize.width, viewport.w])

  const videoDialogWidth = useMemo(() => {
    const stageMaxW = Math.max(16, previewMaxW - VIDEO_SIDEBAR_W - VIDEO_SIDEBAR_GAP)
    const stage = fitRatioSize(stageMaxW, videoMaxH, videoAspect)
    const total = stage.width + VIDEO_SIDEBAR_W + VIDEO_SIDEBAR_GAP
    return Math.min(total, Math.floor(viewport.w * 0.96), 1280 + VIDEO_SIDEBAR_W + VIDEO_SIDEBAR_GAP)
  }, [previewMaxW, videoMaxH, videoAspect, viewport.w])
  const videoStageConfig = useMemo(() => {
    if (!videoCorrection) return null
    return {
      ...videoCorrection,
      aspectRatio: videoAspect,
    }
  }, [videoCorrection, videoAspect])
  const videoHasInk = strokes.length > 0 || texts.length > 0 || arrows.length > 0 || shapes.length > 0

  const viewHeaderActionsMaxWidth = useMemo(() => {
    return Math.max(0, viewDialogWidth - VIEW_HEADER_PAD_X - VIEW_CLOSE_RESERVE - VIEW_ACTIONS_GAP - VIEW_TITLE_RESERVE)
  }, [viewDialogWidth])

  const canvasReady = !(sourceUrl && (sourceAspect == null || sourceAspect <= 0))
  const baseImageLoading = Boolean(baseUrl) && baseImage == null

  const syncBaseImage = useCallback(
    async (url: string | null) => {
      const gen = ++baseLoadGenRef.current
      if (!url) {
        setBaseImage(null)
        setSourceAspect(null)
        setSourceNatural(null)
        return
      }
      setBaseImage(null)
      try {
        const img = await loadSketchImage(url)
        if (gen !== baseLoadGenRef.current) return
        setBaseImage(img)
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          setSourceAspect(img.naturalWidth / img.naturalHeight)
          setSourceNatural({ width: img.naturalWidth, height: img.naturalHeight })
          setLayoutKey("source")
        }
      } catch {
        if (gen !== baseLoadGenRef.current) return
        setBaseImage(null)
        setSourceAspect(null)
        setSourceNatural(null)
        setLayoutKey(normalizeFrameFormat(projectFrameFormat))
      }
    },
    [projectFrameFormat],
  )

  const layoutDropdownValue = useMemo(() => {
    if (layoutKey === "source") {
      return sourceAspect != null && sourceAspect > 0
        ? nearestFrameFormat(sourceAspect)
        : normalizeFrameFormat(projectFrameFormat)
    }
    return layoutKey
  }, [layoutKey, sourceAspect, projectFrameFormat])

  const pushHistory = useCallback(() => {
    if (skipHistoryRef.current) return
    setHistory((prev) => [...prev, { baseUrl, strokes, texts, arrows, shapes }].slice(-HISTORY_LIMIT))
    setFuture([])
  }, [baseUrl, strokes, texts, arrows, shapes])

  const applySnapshot = useCallback(
    (snap: Snapshot) => {
      skipHistoryRef.current = true
      setBaseUrl(snap.baseUrl)
      setStrokes(snap.strokes)
      setTexts(snap.texts)
      setArrows(snap.arrows)
      setShapes(snap.shapes)
      setSelectedIds([])
      setEditingTextId(null)
      setEditingArrowId(null)
      void syncBaseImage(snap.baseUrl)
      queueMicrotask(() => {
        skipHistoryRef.current = false
      })
    },
    [syncBaseImage],
  )

  const undo = useCallback(() => {
    setHistory((prev) => {
      if (prev.length === 0) return prev
      const next = [...prev]
      const snap = next.pop()!
      setFuture((f) => [...f, { baseUrl, strokes, texts, arrows, shapes }].slice(-HISTORY_LIMIT))
      applySnapshot(snap)
      return next
    })
  }, [applySnapshot, baseUrl, strokes, texts, arrows, shapes])

  const redo = useCallback(() => {
    setFuture((prev) => {
      if (prev.length === 0) return prev
      const next = [...prev]
      const snap = next.pop()!
      setHistory((h) => [...h, { baseUrl, strokes, texts, arrows, shapes }].slice(-HISTORY_LIMIT))
      applySnapshot(snap)
      return next
    })
  }, [applySnapshot, baseUrl, strokes, texts, arrows, shapes])

  const measurePane = useCallback(() => {
    const el = paneRef.current
    if (!el) return
    const width = Math.floor(el.clientWidth)
    const height = Math.floor(el.clientHeight)
    if (width < 16 || height < 16) return
    setPaneSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }))
  }, [])

  useLayoutEffect(() => {
    if (!open) return
    measurePane()
    const el = paneRef.current
    if (!el) return
    const ro = new ResizeObserver(() => measurePane())
    ro.observe(el)
    window.addEventListener("resize", measurePane)
    const t0 = window.setTimeout(measurePane, 0)
    const t1 = window.setTimeout(measurePane, 50)
    const t2 = window.setTimeout(measurePane, 200)
    return () => {
      ro.disconnect()
      window.removeEventListener("resize", measurePane)
      window.clearTimeout(t0)
      window.clearTimeout(t1)
      window.clearTimeout(t2)
    }
  }, [open, viewMode, measurePane])

  useEffect(() => {
    if (!open) return
    const onResize = () => {
      setViewport({ w: window.innerWidth, h: window.innerHeight })
    }
    onResize()
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [open])

  useEffect(() => {
    const prev = sizeRef.current
    if (size.width < 16 || size.height < 16) return
    sizeRef.current = size
    if (prev.width < 16 || prev.height < 16) return
    if (prev.width === size.width && prev.height === size.height) return
    const sx = size.width / prev.width
    const sy = size.height / prev.height
    const mapX = (x: number) => x * sx
    const mapY = (y: number) => y * sy
    const brushScale = (sx + sy) / 2
    skipHistoryRef.current = true
    setStrokes((current) => scaleStrokes(current, mapX, mapY, brushScale))
    setTexts((current) => scaleTexts(current, mapX, mapY, brushScale))
    setArrows((current) => scaleArrows(current, mapX, mapY, brushScale))
    setShapes((current) => scaleShapes(current, mapX, mapY, brushScale))
    setCamera(FIT_CAMERA)
    queueMicrotask(() => {
      skipHistoryRef.current = false
    })
  }, [size])

  const tRef = useRef(t)
  tRef.current = t
  const syncBaseImageRef = useRef(syncBaseImage)
  syncBaseImageRef.current = syncBaseImage
  const measurePaneRef = useRef(measurePane)
  measurePaneRef.current = measurePane

  useEffect(() => {
    if (!open) return
    setViewMode(videoMode ? false : mediaViewMode || presentation === "view" || !allowEdit)
    setCopying(false)
    setCamera(FIT_CAMERA)
    setBaseUrl(mediaViewMode ? null : (sourceUrl ?? null))
    setStrokes([])
    setTexts([])
    setArrows([])
    setShapes([])
    setHistory([])
    setFuture([])
    setTool("pen")
    setPenFlyoutOpen(false)
    setEraserFlyoutOpen(false)
    setShapeFlyoutOpen(false)
    setArrowFlyoutOpen(false)
    setAiFlyoutOpen(false)
    setMoreMenuOpen(false)
    setPenKind(DEFAULT_PEN)
    setTipSettings(defaultTipSettings())
    setEraserWidth(DEFAULT_ERASER_WIDTH)
    setShapeKind(DEFAULT_SHAPE_KIND)
    setShapeColor(DEFAULT_COLOR)
    setShapeWidth(DEFAULT_SHAPE_WIDTH)
    setArrowColor(DEFAULT_COLOR)
    setArrowWidth(DEFAULT_ARROW_WIDTH)
    setArrowLabelColor(DEFAULT_COLOR)
    setArrowLabelWidth(DEFAULT_ARROW_WIDTH)
    setTextColor(DEFAULT_COLOR)
    setSelectedIds([])
    setEditingTextId(null)
    setEditingArrowId(null)
    setPrompt("")
    setAiBusy(false)
    setPendingJobId(null)
    setPendingEditId(null)
    setCompletedEditId(null)
    finishingRef.current = false
    setConfirmBusy(false)
    setAiLibraryRef(null)
    setAiSourceUrl(null)
    const nextName =
      initialFileName?.trim() ||
      headerTitle?.trim() ||
      tRef.current("canvas.sketch.defaultFileName", { number: nextSketchNumber() })
    setFileName(nextName)
    setTitleDraft(nextName)
    setEditingTitle(false)
    setSourceAspect(videoViewMode ? 16 / 9 : null)
    setSourceNatural(null)
    setBaseImage(null)
    sizeRef.current = { width: 0, height: 0 }

    if (videoViewMode || audioViewMode) {
      setLayoutKey("source")
      void syncBaseImageRef.current(null)
    } else if (sourceUrl) {
      setLayoutKey("source")
      void syncBaseImageRef.current(sourceUrl)
    } else {
      setLayoutKey(normalizeFrameFormat(projectFrameFormat))
      void syncBaseImageRef.current(null)
    }

    const pending = getSketchEditWaiter()
    if (pending && pending.ownerId === ownerId && pending.projectId === projectId) {
      const job = useWorkspaceJobsStore.getState().jobs.find((j) => j.id === pending.jobId)
      const status = job?.status
      if (!status || status === "queued" || status === "processing") {
        setAiBusy(true)
        setPendingJobId(pending.jobId)
        setPendingEditId(pending.editId)
        onCorrectionPendingChangeRef.current?.({ jobId: pending.jobId, active: true })
      } else {
        clearSketchEditWaiter(pending.jobId)
      }
    }

    requestAnimationFrame(() => measurePaneRef.current())
    return () => {
      baseLoadGenRef.current += 1
    }
    // t / syncBaseImage / measurePane via refs — resize must not snap edit back to view
  }, [
    open,
    sourceUrl,
    videoUrl,
    audioUrl,
    projectFrameFormat,
    projectId,
    initialFileName,
    headerTitle,
    presentation,
    allowEdit,
    ownerId,
    videoMode,
    videoViewMode,
    audioViewMode,
    mediaViewMode,
  ])

  useEffect(() => {
    if (!allowEdit) setViewMode(true)
  }, [allowEdit])

  useEffect(() => {
    setVideoCut(null)
    setVideoFileDurationMs(videoDurationMs != null && videoDurationMs > 0 ? videoDurationMs : 0)
    setVideoCutSaving(false)
  }, [open, videoUrl, videoDurationMs])

  const handleVideoDocChange = useCallback((doc: TimelineDocument, meta?: { reason: "edit" | "metadata" }) => {
    const clip = doc.tracks.find((track) => track.kind === "video")?.clips[0]
    if (!clip) return
    if (meta?.reason !== "edit") {
      if (clip.source_in_ms === 0 && clip.source_out_ms > 0) {
        setVideoFileDurationMs((prev) => Math.max(prev, clip.source_out_ms))
      }
      return
    }
    setVideoCut({ inMs: clip.source_in_ms, outMs: clip.source_out_ms })
  }, [])

  const videoCutDirty =
    onSaveVideoCut != null &&
    videoFileDurationMs > 0 &&
    videoCut != null &&
    (videoCut.inMs > 0 || videoCut.outMs < videoFileDurationMs)

  const handleSaveVideoCut = useCallback(async () => {
    if (!onSaveVideoCut || !videoCut || videoCutSaving) return
    if (!(videoFileDurationMs > 0) || (videoCut.inMs <= 0 && videoCut.outMs >= videoFileDurationMs)) return
    setVideoCutSaving(true)
    try {
      await onSaveVideoCut({ sourceInMs: videoCut.inMs, sourceOutMs: videoCut.outMs })
      onOpenChange(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("canvas.sketch.confirmFailed"))
      setVideoCutSaving(false)
    }
  }, [onOpenChange, onSaveVideoCut, t, videoCut, videoCutSaving, videoFileDurationMs])

  // Video view: keep focus on the player so Space hits play (Radix otherwise focuses Close).
  useEffect(() => {
    if (!open || !videoViewMode) return
    const id = window.setTimeout(() => {
      paneRef.current?.focus({ preventScroll: true })
    }, 0)
    return () => window.clearTimeout(id)
  }, [open, videoViewMode, videoUrl])

  // Audio view: focus player so Space hits play (Radix otherwise focuses Close).
  useEffect(() => {
    if (!open || !audioViewMode) return
    const id = window.setTimeout(() => {
      viewAudioRef.current?.focus({ preventScroll: true })
    }, 0)
    return () => window.clearTimeout(id)
  }, [open, audioViewMode, audioUrl])

  useEffect(() => {
    if (!editingTitle) return
    titleInputRef.current?.focus()
    titleInputRef.current?.select()
  }, [editingTitle])

  const commitTitleEdit = useCallback(() => {
    const next = titleDraft.trim() || fileName
    if (next) {
      setFileName(next)
      setTitleDraft(next)
    }
    setEditingTitle(false)
  }, [titleDraft, fileName])

  const deleteSelected = useCallback(() => {
    if (selectedIds.length === 0) return
    const idSet = new Set(selectedIds)
    pushHistory()
    setStrokes((prev) => prev.filter((item) => !idSet.has(item.id)))
    setTexts((prev) => prev.filter((item) => !idSet.has(item.id)))
    setArrows((prev) => prev.filter((item) => !idSet.has(item.id)))
    setShapes((prev) => prev.filter((item) => !idSet.has(item.id)))
    setSelectedIds([])
    setEditingTextId(null)
    setEditingArrowId(null)
  }, [selectedIds, pushHistory])

  const applyZoomPreset = useCallback(
    (preset: SketchZoomPreset) => {
      setCamera(cameraForPreset(preset, sourceNatural, size))
    },
    [sourceNatural, size],
  )

  const nudgeCameraZoom = useCallback(
    (factor: number) => {
      setCamera((prev) =>
        zoomAt(prev, { x: size.width / 2, y: size.height / 2 }, prev.zoom * factor, size.width, size.height),
      )
    },
    [size],
  )

  useEffect(() => {
    if (!open || viewMode) return
    setCamera(FIT_CAMERA)
  }, [open, viewMode])

  const closeFlyouts = () => {
    setPenFlyoutOpen(false)
    setEraserFlyoutOpen(false)
    setShapeFlyoutOpen(false)
    setArrowFlyoutOpen(false)
    setAiFlyoutOpen(false)
    setMoreMenuOpen(false)
  }

  useLayoutEffect(() => {
    if (viewMode || !open) return
    const root = toolbarRootRef.current
    const measure = toolbarMeasureRef.current
    if (!root || !measure || typeof ResizeObserver === "undefined") return

    const recompute = () => {
      const available = root.clientWidth
      const widths = {
        icon: measuredOuterWidth(
          measure.querySelector("[data-edit-measure='icon']"),
          FALLBACK_EDIT_TOOLBAR_WIDTHS.icon,
        ),
        format: measuredOuterWidth(
          measure.querySelector("[data-edit-measure='format']"),
          FALLBACK_EDIT_TOOLBAR_WIDTHS.format,
        ),
        resolution: measuredOuterWidth(
          measure.querySelector("[data-edit-measure='resolution']"),
          FALLBACK_EDIT_TOOLBAR_WIDTHS.resolution,
        ),
        divider: measuredOuterWidth(
          measure.querySelector("[data-edit-measure='divider']"),
          FALLBACK_EDIT_TOOLBAR_WIDTHS.divider,
        ),
      }
      const fit = computeEditToolbarFit(available, Boolean(sourceNatural), widths, !isDesktop)
      setEditOverflowHidden((prev) => (editOverflowSetsEqual(prev, fit.hidden) ? prev : fit.hidden))
      setEditShowResolution(fit.showResolution)
      setEditShowFormat(fit.showFormat)
    }

    recompute()
    const observer = new ResizeObserver(recompute)
    observer.observe(root)
    observer.observe(measure)
    return () => observer.disconnect()
  }, [open, sourceNatural, viewMode, isDesktop])

  useEffect(() => {
    if (editOverflowHidden.size === 0 && editShowFormat && moreMenuOpen) setMoreMenuOpen(false)
  }, [editOverflowHidden, editShowFormat, moreMenuOpen])

  useEffect(() => {
    if (isDesktop) setAiFlyoutOpen(false)
  }, [isDesktop])

  useEffect(() => {
    if (!open || viewMode) return
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const tag = target?.tagName?.toLowerCase()
      const inField = tag === "input" || tag === "textarea" || Boolean(target?.isContentEditable)
      const noMod = !e.metaKey && !e.ctrlKey && !e.altKey

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z" && !e.shiftKey) {
        if (aiBusy || confirmBusy) return
        e.preventDefault()
        undo()
        return
      }

      if ((e.metaKey || e.ctrlKey) && ((e.key.toLowerCase() === "z" && e.shiftKey) || e.key.toLowerCase() === "y")) {
        if (aiBusy || confirmBusy) return
        e.preventDefault()
        redo()
        return
      }

      if (inField) {
        const promptField = target?.closest("[data-sketch-ai-prompt]")
        if (tag === "textarea" && e.key === "Enter" && promptField) {
          const modEnterOnly =
            promptField instanceof HTMLElement && promptField.hasAttribute("data-sketch-ai-mod-enter")
          const shouldApply = modEnterOnly ? e.metaKey || e.ctrlKey : !e.shiftKey && !e.metaKey && !e.ctrlKey
          if (shouldApply) {
            e.preventDefault()
            void runAiEditRef.current()
          }
        }
        return
      }

      if (noMod && (e.key === "f" || e.key === "F")) {
        e.preventDefault()
        applyZoomPreset("fit")
        return
      }
      if (noMod && e.key === "1") {
        e.preventDefault()
        applyZoomPreset("100")
        return
      }
      if (noMod && e.key === "2") {
        e.preventDefault()
        applyZoomPreset("200")
        return
      }
      if (noMod && (e.key === "=" || e.key === "+" || e.code === "NumpadAdd")) {
        e.preventDefault()
        nudgeCameraZoom(SKETCH_ZOOM_STEP)
        return
      }
      if (noMod && (e.key === "-" || e.key === "_" || e.code === "NumpadSubtract")) {
        e.preventDefault()
        nudgeCameraZoom(1 / SKETCH_ZOOM_STEP)
        return
      }

      if (aiBusy || confirmBusy) return

      if (e.key === "v" || e.key === "V") {
        e.preventDefault()
        setTool("select")
        closeFlyouts()
      } else if (e.key === "b" || e.key === "B") {
        e.preventDefault()
        setTool("pen")
        setPenFlyoutOpen(true)
        setEraserFlyoutOpen(false)
        setShapeFlyoutOpen(false)
        setArrowFlyoutOpen(false)
      } else if (e.key === "e" || e.key === "E") {
        e.preventDefault()
        setTool("eraser")
        setPenFlyoutOpen(false)
        setShapeFlyoutOpen(false)
        setEraserFlyoutOpen(true)
        setArrowFlyoutOpen(false)
      } else if (e.key === "o" || e.key === "O") {
        e.preventDefault()
        setTool("shape")
        setPenFlyoutOpen(false)
        setEraserFlyoutOpen(false)
        setShapeFlyoutOpen(true)
        setArrowFlyoutOpen(false)
      } else if (e.key === "a" || e.key === "A") {
        e.preventDefault()
        setTool("arrow")
        setPenFlyoutOpen(false)
        setEraserFlyoutOpen(false)
        setShapeFlyoutOpen(false)
        setArrowFlyoutOpen(true)
      } else if (e.key === "l" || e.key === "L") {
        e.preventDefault()
        setTool("arrowLabel")
        setPenFlyoutOpen(false)
        setEraserFlyoutOpen(false)
        setShapeFlyoutOpen(false)
        setArrowFlyoutOpen(true)
      } else if (e.key === "t" || e.key === "T") {
        e.preventDefault()
        setTool("text")
        closeFlyouts()
      } else if (
        (e.key === "Backspace" || e.key === "Delete") &&
        selectedIds.length > 0 &&
        !editingTextId &&
        !editingArrowId
      ) {
        e.preventDefault()
        deleteSelected()
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [
    open,
    viewMode,
    aiBusy,
    confirmBusy,
    undo,
    redo,
    selectedIds,
    editingTextId,
    editingArrowId,
    deleteSelected,
    applyZoomPreset,
    nudgeCameraZoom,
  ])

  useEffect(() => {
    if (!open || !videoMode) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== "Enter") return
      e.preventDefault()
      e.stopPropagation()
      video.submitCorrection()
    }
    window.addEventListener("keydown", onKeyDown, true)
    return () => window.removeEventListener("keydown", onKeyDown, true)
  }, [open, videoMode, video])

  const dirty =
    strokes.length > 0 ||
    texts.length > 0 ||
    arrows.length > 0 ||
    shapes.length > 0 ||
    history.length > 0 ||
    Boolean(prompt.trim())

  const hasCanvasEdits =
    strokes.length > 0 || texts.length > 0 || arrows.length > 0 || shapes.length > 0 || history.length > 0

  const librarySaveDisabled =
    aiBusy || confirmBusy || (!showConfirmPersist && showLibraryPersist && !hasCanvasEdits && !allowCleanLibrarySave)

  const primaryConfirmLabel =
    confirmLabel ?? (confirmAsSave ? t("canvas.sketch.confirmSave") : t("canvas.sketch.useAsReference"))

  const requestClose = useCallback(
    async (nextOpen: boolean) => {
      if (nextOpen) {
        onOpenChange(true)
        return
      }
      if (confirmBusy) return
      if (!aiBusy && dirty && persist !== "none") {
        const ok = await confirm({
          title: t("canvas.sketch.discardTitle"),
          message: t("canvas.sketch.discardMessage"),
          confirmLabel: t("canvas.sketch.discardConfirm"),
          cancelLabel: t("cancel", { ns: "common" }),
          tone: "danger",
        })
        if (!ok) return
      }
      // Detach from in-flight correction: job keeps running; toast/jobs panel own the result.
      if (aiBusy) {
        aiSessionRef.current += 1
        if (pendingJobId != null) clearSketchEditWaiter(pendingJobId)
        else clearSketchEditWaiter()
        setAiBusy(false)
        setPendingJobId(null)
        setPendingEditId(null)
      }
      onOpenChange(false)
    },
    [aiBusy, confirmBusy, dirty, onOpenChange, pendingJobId, persist, t],
  )

  const buildExportBlob = useCallback(async () => {
    const xf = buildExportTransform(size, sourceNatural, Boolean(baseUrl))
    return flattenSketchToBlob({
      width: xf.width,
      height: xf.height,
      baseUrl,
      baseImage,
      background: "white",
      imageFit: xf.imageFit,
      strokes: scaleStrokes(strokes, xf.mapX, xf.mapY, xf.brushScale),
      texts: scaleTexts(texts, xf.mapX, xf.mapY, xf.brushScale),
      arrows: scaleArrows(arrows, xf.mapX, xf.mapY, xf.brushScale),
      shapes: scaleShapes(shapes, xf.mapX, xf.mapY, xf.brushScale),
    })
  }, [size, sourceNatural, baseUrl, baseImage, strokes, texts, arrows, shapes])

  const handleDownloadOriginal = useCallback(async () => {
    if (confirmBusy) return
    try {
      const blob = await buildExportBlob()
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = objectUrl
      a.download = toSketchFileName(fileName, "sketch")
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(objectUrl)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("canvas.sketch.confirmFailed"))
    }
  }, [buildExportBlob, confirmBusy, fileName, t])

  const handleViewDownload = useCallback(() => {
    const url = videoViewMode ? videoUrl?.trim() : audioViewMode ? audioUrl?.trim() : baseUrl
    if (!url) return
    if (videoViewMode || audioViewMode) {
      const a = document.createElement("a")
      a.href = url
      a.download = toSketchFileName(fileName, videoViewMode ? "previz" : "audio")
      a.rel = "noopener"
      document.body.appendChild(a)
      a.click()
      a.remove()
      return
    }
    void downloadImage(url, toSketchFileName(fileName, "sketch"))
  }, [audioUrl, audioViewMode, baseUrl, fileName, videoUrl, videoViewMode])

  const handleViewCopy = useCallback(async () => {
    if (mediaViewMode || !baseUrl || copying) return
    setCopying(true)
    try {
      await copyImageToClipboard(baseUrl)
      toast.success(t("imageLightbox.copySuccess", { ns: "common" }))
    } catch {
      toast.error(t("imageLightbox.copyError", { ns: "common" }))
    } finally {
      setCopying(false)
    }
  }, [baseUrl, copying, mediaViewMode, t])

  const runAiEdit = async () => {
    const text = prompt.trim()
    if (!allowEdit || viewMode || !text || aiBusy) return
    const session = ++aiSessionRef.current
    setAiBusy(true)
    setCompletedEditId(null)
    setAiLibraryRef(null)
    setAiFlyoutOpen(false)
    try {
      const blob = await buildExportBlob()
      if (session !== aiSessionRef.current) return
      const sketchEditPayload = {
        image: blob,
        prompt: text,
        filename: toSketchFileName(fileName, "sketch"),
        aspect_ratio: aspectRatioForLayout(layoutKey, size.width, size.height, sourceAspect),
        shot_id: replaceShotOnCorrect?.shotId,
        sketch_shot_tab: replaceShotOnCorrect?.tab,
        canvas_id: replaceCanvasOnCorrect?.canvasId,
        source_generation_id: replaceCanvasOnCorrect?.sourceGenerationId,
        character_id: replaceCharacterOnCorrect?.characterId,
        sketch_character_target: replaceCharacterOnCorrect?.target,
        view: replaceCharacterOnCorrect?.view,
        sheet_scope: replaceCharacterOnCorrect?.sheetScope,
        outfit_id: replaceCharacterOnCorrect?.outfitId,
        prop_id: replacePropOnCorrect?.propId,
        sketch_prop_target: replacePropOnCorrect?.target,
        state_id: replacePropOnCorrect?.stateId,
        location_id: replaceLocationOnCorrect?.locationId,
        variant_id: replaceLocationOnCorrect?.variantId,
        facing: replaceLocationOnCorrect?.facing,
      }
      const replaceEntityOnCorrect = Boolean(
        replaceCanvasOnCorrect ||
        replaceShotOnCorrect ||
        replaceCharacterOnCorrect ||
        replacePropOnCorrect ||
        replaceLocationOnCorrect,
      )
      if (replaceEntityOnCorrect) {
        const clientId = -Date.now()
        if (replaceCanvasOnCorrect) {
          onCanvasCorrectionOptimistic?.(replaceCanvasOnCorrect.sourceGenerationId, clientId)
        }
        if (replaceShotOnCorrect || replaceCharacterOnCorrect || replacePropOnCorrect || replaceLocationOnCorrect) {
          onCorrectionPendingChangeRef.current?.({ jobId: clientId, active: true })
        }
        setAiBusy(false)
        setPendingJobId(null)
        setPendingEditId(null)
        onOpenChange(false)
        void postSketchEdit(projectId, sketchEditPayload)
          .then((enqueued) => {
            if (replaceCanvasOnCorrect) {
              if (!enqueued.generation) throw new Error(t("canvas.sketch.confirmFailed"))
              onCanvasCorrectionQueued?.(enqueued.generation, clientId)
              return
            }
            onCorrectionPendingChangeRef.current?.({ jobId: enqueued.generation_job_id, active: true })
          })
          .catch((e) => {
            if (replaceCanvasOnCorrect) {
              onCanvasCorrectionEnqueueFailed?.(clientId)
            }
            if (replaceShotOnCorrect || replaceCharacterOnCorrect || replacePropOnCorrect || replaceLocationOnCorrect) {
              onCorrectionPendingChangeRef.current?.({ jobId: clientId, active: false })
            }
            toast.error(e instanceof Error ? e.message : t("canvas.sketch.confirmFailed"))
          })
        return
      }
      const enqueued = await postSketchEdit(projectId, sketchEditPayload)
      if (session !== aiSessionRef.current) return
      onCorrectionPendingChangeRef.current?.({ jobId: enqueued.generation_job_id, active: true })
      setSketchEditWaiter({
        ownerId,
        projectId,
        jobId: enqueued.generation_job_id,
        editId: enqueued.sketch_edit_id,
      })
      setPendingJobId(enqueued.generation_job_id)
      setPendingEditId(enqueued.sketch_edit_id)
    } catch (e) {
      if (session !== aiSessionRef.current) return
      clearSketchEditWaiter()
      setAiBusy(false)
      setPendingJobId(null)
      setPendingEditId(null)
      toast.error(e instanceof Error ? e.message : t("canvas.sketch.confirmFailed"))
    }
  }

  const runAiEditRef = useRef(runAiEdit)
  runAiEditRef.current = runAiEdit

  const pendingJobStatus = useWorkspaceJobsStore((s) => {
    if (pendingJobId == null) return null
    return s.jobs.find((j) => j.id === pendingJobId)?.status ?? null
  })

  useEffect(() => {
    if (!aiBusy || pendingEditId == null) return

    const session = aiSessionRef.current
    const stillMine = () => session === aiSessionRef.current
    let alive = true
    let pollTimer: number | null = null

    const clearAiPending = () => {
      if (pendingJobId != null) clearSketchEditWaiter(pendingJobId)
      else clearSketchEditWaiter()
      setAiBusy(false)
      setPendingJobId(null)
      setPendingEditId(null)
    }

    const finish = async (terminal: "completed" | "failed" | "cancelled") => {
      if (finishingRef.current) return
      if (!alive) return
      finishingRef.current = true
      alive = false
      if (pollTimer != null) window.clearInterval(pollTimer)

      const jobId = pendingJobId
      const editId = pendingEditId
      // Drop busy/pending before awaits so a jobs-store status flip cannot re-enter finish.
      clearAiPending()

      try {
        if (!stillMine()) return

        if (terminal === "cancelled") {
          if (jobId != null) onCorrectionPendingChangeRef.current?.({ jobId, active: false })
          toast.error(t("canvas.sketch.editCancelled"))
          return
        }
        if (terminal === "failed") {
          if (jobId != null) onCorrectionPendingChangeRef.current?.({ jobId, active: false })
          const job = jobId != null ? useWorkspaceJobsStore.getState().jobs.find((j) => j.id === jobId) : undefined
          toast.error(job?.error_message?.trim() || t("canvas.sketch.editFailed"))
          return
        }

        if (editId == null) return
        const result = await fetchSketchEdit(projectId, editId)
        if (!stillMine()) return
        if (result.status === "failed" || !result.url) {
          if (jobId != null) onCorrectionPendingChangeRef.current?.({ jobId, active: false })
          toast.error(result.error?.trim() || t("canvas.sketch.editFailed"))
          return
        }
        pushHistory()
        setBaseUrl(result.url)
        setAiSourceUrl(result.url)
        setStrokes([])
        setTexts([])
        setArrows([])
        setShapes([])
        setSelectedIds([])
        setEditingTextId(null)
        setEditingArrowId(null)
        setAiLibraryRef(null)
        setCompletedEditId(editId)
        await syncBaseImage(result.url)
        if (!stillMine()) return
        if (jobId != null) onCorrectionPendingChangeRef.current?.({ jobId, active: false })
      } catch (e) {
        if (!stillMine()) return
        if (jobId != null) onCorrectionPendingChangeRef.current?.({ jobId, active: false })
        toast.error(e instanceof Error ? e.message : t("canvas.sketch.editFailed"))
      } finally {
        finishingRef.current = false
      }
    }

    if (pendingJobStatus === "completed" || pendingJobStatus === "failed" || pendingJobStatus === "cancelled") {
      void finish(pendingJobStatus)
      return () => {
        alive = false
      }
    }

    const pollOnce = async () => {
      try {
        const result = await fetchSketchEdit(projectId, pendingEditId)
        if (!alive) return
        if (result.status === "failed") {
          await finish("failed")
          return
        }
        if (result.status === "completed") {
          await finish("completed")
        }
      } catch {
        // keep waiting; jobs store may still reach a terminal status
      }
    }

    pollTimer = window.setInterval(() => {
      void pollOnce()
    }, 2000)
    void pollOnce()

    return () => {
      alive = false
      if (pollTimer != null) window.clearInterval(pollTimer)
    }
    // pushHistory / fileName / onCorrectionPendingChange via ref
    // eslint-disable-next-line react-hooks/exhaustive-deps -- wait only on job terminal status + poll
  }, [aiBusy, pendingJobId, pendingEditId, pendingJobStatus, projectId, t])

  const exportSketchFile = async () => {
    const blob = await buildExportBlob()
    return new File([blob], toSketchFileName(fileName, "sketch"), { type: "image/png" })
  }

  const isCleanAiReuse = () => {
    const markupEmpty = strokes.length === 0 && texts.length === 0 && arrows.length === 0 && shapes.length === 0
    return markupEmpty && aiSourceUrl != null && baseUrl === aiSourceUrl && completedEditId != null
  }

  const ensureAiLibraryRef = useCallback(async (): Promise<LibrarySketchItem> => {
    if (aiLibraryRef) return aiLibraryRef
    if (completedEditId == null) {
      throw new Error(t("canvas.sketch.confirmFailed"))
    }
    const ref = await promoteSketchEditToLibrary(projectId, completedEditId)
    setAiLibraryRef(ref)
    return ref
  }, [aiLibraryRef, completedEditId, projectId, t])

  const handleConfirm = async () => {
    if (confirmBusy || aiBusy || !onConfirm) return
    setConfirmBusy(true)
    try {
      if (isCleanAiReuse() && onConfirmExistingLibraryRef) {
        const ref = await ensureAiLibraryRef()
        await onConfirmExistingLibraryRef(ref)
        onOpenChange(false)
        return
      }

      const file = await exportSketchFile()
      await onConfirm(file)
      onOpenChange(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("canvas.sketch.confirmFailed"))
    } finally {
      setConfirmBusy(false)
    }
  }

  const handleSaveToLibrary = async () => {
    if (confirmBusy || aiBusy || !onSaveToLibrary) return
    setConfirmBusy(true)
    try {
      if (isCleanAiReuse()) {
        const ref = await ensureAiLibraryRef()
        if (onSaveExistingToLibrary) {
          await onSaveExistingToLibrary(ref)
        } else {
          await onLibrarySaved?.()
        }
        onOpenChange(false)
        return
      }

      const file = await exportSketchFile()
      await onSaveToLibrary(file)
      onOpenChange(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("canvas.sketch.confirmFailed"))
    } finally {
      setConfirmBusy(false)
    }
  }

  const sketchAvailable = Boolean(sketchEdit?.available)
  const correctionUnavailableReason = t("canvas.sketch.correctionUnavailable")
  const aiToolbarTitle = sketchAvailable ? t("canvas.sketch.aiHeading") : correctionUnavailableReason
  const aiFormProps = {
    prompt,
    onPromptChange: setPrompt,
    onApply: () => void runAiEdit(),
    busy: aiBusy,
    disabled: confirmBusy,
    sketchAvailable,
    modelLabel: sketchEdit?.modelLabel,
    disabledReason: sketchAvailable ? undefined : correctionUnavailableReason,
    quickPrompts: [
      t("canvas.sketch.quickPrompts.removeCircled"),
      t("canvas.sketch.quickPrompts.removeBackground"),
      t("canvas.sketch.quickPrompts.remakeArrowTarget"),
    ],
    labels: {
      heading: t("canvas.sketch.aiHeading"),
      placeholder: t("canvas.sketch.promptPlaceholder"),
      apply: t("canvas.sketch.applyAi"),
      applying: t("canvas.sketch.applyingAi"),
    },
    confirmDelayKey: resolvedAiConfirmDelayKey,
  }

  const applyPenKind = (kind: PenKind) => {
    setPenKind(kind)
    setTool("pen")
    setEraserFlyoutOpen(false)
    setShapeFlyoutOpen(false)
    setArrowFlyoutOpen(false)
    setMoreMenuOpen(false)
  }

  const showHeaderActions = viewMode && (Boolean(baseUrl) || mediaViewMode)
  const showEditEntry = allowEdit && viewMode && !mediaViewMode && (persist !== "none" || sketchEdit != null)
  const overflowToolActive = isEditOverflowKey(tool) && editOverflowHidden.has(tool)
  const showEraser = !editOverflowHidden.has("eraser")
  const showShape = !editOverflowHidden.has("shape")
  const showArrow = !editOverflowHidden.has("arrow")
  const showArrowLabel = !editOverflowHidden.has("arrowLabel")
  const showText = !editOverflowHidden.has("text")
  const showUndo = !editOverflowHidden.has("undo")
  const showRedo = !editOverflowHidden.has("redo")
  const showDownload = !editOverflowHidden.has("download")
  const showCopy = !editOverflowHidden.has("copy")
  const showHistory = showUndo || showRedo

  const pickEraser = () => {
    setPenFlyoutOpen(false)
    setShapeFlyoutOpen(false)
    setArrowFlyoutOpen(false)
    setAiFlyoutOpen(false)
    setMoreMenuOpen(false)
    if (tool === "eraser") {
      setEraserFlyoutOpen((v) => !v)
    } else {
      setTool("eraser")
      setEraserFlyoutOpen(true)
    }
  }
  const pickShape = () => {
    setPenFlyoutOpen(false)
    setEraserFlyoutOpen(false)
    setArrowFlyoutOpen(false)
    setAiFlyoutOpen(false)
    setMoreMenuOpen(false)
    if (tool === "shape") {
      setShapeFlyoutOpen((v) => !v)
    } else {
      setTool("shape")
      setShapeFlyoutOpen(true)
    }
  }
  const pickArrow = () => {
    setPenFlyoutOpen(false)
    setEraserFlyoutOpen(false)
    setShapeFlyoutOpen(false)
    setAiFlyoutOpen(false)
    setMoreMenuOpen(false)
    if (tool === "arrow") {
      setArrowFlyoutOpen((v) => !v)
    } else {
      setTool("arrow")
      setArrowFlyoutOpen(true)
    }
  }
  const pickArrowLabel = () => {
    setPenFlyoutOpen(false)
    setEraserFlyoutOpen(false)
    setShapeFlyoutOpen(false)
    setAiFlyoutOpen(false)
    setMoreMenuOpen(false)
    if (tool === "arrowLabel") {
      setArrowFlyoutOpen((v) => !v)
    } else {
      setTool("arrowLabel")
      setArrowFlyoutOpen(true)
    }
  }
  const pickText = () => {
    setPenFlyoutOpen(false)
    setEraserFlyoutOpen(false)
    setShapeFlyoutOpen(false)
    setArrowFlyoutOpen(false)
    setAiFlyoutOpen(false)
    setMoreMenuOpen(false)
    setTool("text")
  }

  const eraserFlyout = (
    <SketchWidthFlyout
      open={eraserFlyoutOpen && tool === "eraser"}
      width={eraserWidth}
      weightLabel={t("canvas.sketch.weight")}
      onWidthChange={setEraserWidth}
      onClose={() => setEraserFlyoutOpen(false)}
      anchorRef={showEraser ? eraserButtonRef : moreButtonRef}
    />
  )
  const shapeFlyout = (
    <SketchShapeFlyout
      open={shapeFlyoutOpen && tool === "shape"}
      shapeKind={shapeKind}
      color={shapeColor}
      shapeWidth={shapeWidth}
      labels={{
        rect: t("canvas.sketch.shapeRect"),
        ellipse: t("canvas.sketch.shapeEllipse"),
        color: t("canvas.sketch.penColor"),
        weight: t("canvas.sketch.weight"),
      }}
      onShapeKindChange={(kind) => {
        setShapeKind(kind)
        setTool("shape")
      }}
      onColorChange={setShapeColor}
      onWidthChange={setShapeWidth}
      onClose={() => setShapeFlyoutOpen(false)}
      anchorRef={showShape ? shapeButtonRef : moreButtonRef}
    />
  )
  const arrowFlyout = (
    <SketchArrowFlyout
      open={arrowFlyoutOpen && tool === "arrow"}
      color={arrowColor}
      arrowWidth={arrowWidth}
      labels={{
        color: t("canvas.sketch.penColor"),
        weight: t("canvas.sketch.weight"),
      }}
      onColorChange={setArrowColor}
      onWidthChange={setArrowWidth}
      onClose={() => setArrowFlyoutOpen(false)}
      anchorRef={showArrow ? arrowButtonRef : moreButtonRef}
    />
  )
  const arrowLabelFlyout = (
    <SketchArrowFlyout
      open={arrowFlyoutOpen && tool === "arrowLabel"}
      color={arrowLabelColor}
      arrowWidth={arrowLabelWidth}
      labels={{
        color: t("canvas.sketch.penColor"),
        weight: t("canvas.sketch.weight"),
      }}
      onColorChange={setArrowLabelColor}
      onWidthChange={setArrowLabelWidth}
      onClose={() => setArrowFlyoutOpen(false)}
      anchorRef={showArrowLabel ? arrowLabelButtonRef : moreButtonRef}
    />
  )

  const moreItems = [
    {
      key: "eraser",
      label: t("canvas.sketch.toolEraser"),
      icon: <Eraser className="h-4 w-4" />,
      onClick: pickEraser,
    },
    {
      key: "shape",
      label: t("canvas.sketch.toolObjects"),
      icon: shapeKind === "ellipse" ? <Circle className="h-4 w-4" /> : <Square className="h-4 w-4" />,
      onClick: pickShape,
    },
    {
      key: "arrow",
      label: t("canvas.sketch.toolArrow"),
      icon: <ArrowUpRight className="h-4 w-4" />,
      onClick: pickArrow,
    },
    {
      key: "arrowLabel",
      label: t("canvas.sketch.toolArrowLabel"),
      icon: (
        <span className="relative inline-flex h-4 w-4 items-center justify-center">
          <ArrowUpRight className="absolute top-0 left-0 h-3.5 w-3.5" />
          <Type className="absolute right-0 bottom-0 h-2 w-2" />
        </span>
      ),
      onClick: pickArrowLabel,
    },
    {
      key: "text",
      label: t("canvas.sketch.toolText"),
      icon: <Type className="h-4 w-4" />,
      onClick: pickText,
    },
    {
      key: "undo",
      label: t("canvas.sketch.undo"),
      icon: <Undo2 className="h-4 w-4" />,
      onClick: undo,
      disabled: history.length === 0,
    },
    {
      key: "redo",
      label: t("canvas.sketch.redo"),
      icon: <Redo2 className="h-4 w-4" />,
      onClick: redo,
      disabled: future.length === 0,
    },
    {
      key: "download",
      label: t("imageLightbox.downloadOriginal", { ns: "common" }),
      icon: <Download className="h-4 w-4" />,
      onClick: () => void handleDownloadOriginal(),
      disabled: confirmBusy,
    },
    {
      key: "copy",
      label: t("imageLightbox.copy", { ns: "common" }),
      icon: copying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />,
      onClick: () => void handleViewCopy(),
      disabled: confirmBusy || copying || !baseUrl,
    },
  ].filter((item) => isEditOverflowKey(item.key) && editOverflowHidden.has(item.key))

  const formatOverflow = !editShowFormat
  const formatDropdown = (
    <FrameFormatDropdown
      value={layoutDropdownValue}
      fillOption={{ label: t("canvas.sketch.layoutFill") }}
      disabled={aiBusy || confirmBusy}
      ariaLabel={t("canvas.sketch.layoutLabel")}
      onChange={(value) => {
        setLayoutKey(value)
        if (formatOverflow) setMoreMenuOpen(false)
      }}
      className={formatOverflow ? "w-full min-w-[7.5rem]" : "w-auto min-w-[7.5rem]"}
    />
  )

  const toolbar = (
    <div ref={toolbarRootRef} className="flex w-full min-w-0 items-center justify-center">
      <div
        ref={toolbarMeasureRef}
        className="pointer-events-none fixed top-0 left-0 -z-50 flex items-center gap-1.5 opacity-0"
        aria-hidden
      >
        <span data-edit-measure="icon" className="inline-flex h-8 w-8" />
        <span data-edit-measure="format" className="inline-block h-8 min-w-[7.5rem]" />
        <span data-edit-measure="resolution" className="shrink-0 px-1 text-xs text-ui-text-secondary tabular-nums">
          {sourceNatural ? (
            <>
              {sourceNatural.width}&times;{sourceNatural.height}
            </>
          ) : (
            "0000×0000"
          )}
        </span>
        <span data-edit-measure="divider" className="mx-1 h-5 w-px bg-ui-border" />
      </div>
      <div className="flex flex-nowrap items-center justify-center gap-1.5">
        <EditToolbarButton
          active={tool === "select"}
          title={t("canvas.sketch.toolSelect")}
          onClick={() => {
            setTool("select")
            closeFlyouts()
            setMoreMenuOpen(false)
          }}
        >
          <MousePointer2 className="h-4 w-4" />
        </EditToolbarButton>

        <div className="relative">
          <EditToolbarButton
            ref={penButtonRef}
            active={tool === "pen"}
            title={t("canvas.sketch.toolPen")}
            onClick={() => {
              setPenFlyoutOpen(false)
              setEraserFlyoutOpen(false)
              setShapeFlyoutOpen(false)
              setArrowFlyoutOpen(false)
              setAiFlyoutOpen(false)
              setMoreMenuOpen(false)
              if (tool === "pen") {
                setPenFlyoutOpen((v) => !v)
              } else {
                setTool("pen")
                setPenFlyoutOpen(true)
              }
            }}
          >
            <PenLine className="h-4 w-4" />
          </EditToolbarButton>
          <SketchPenFlyout
            open={penFlyoutOpen && tool === "pen"}
            penKind={penKind}
            color={penColor}
            tipColors={{
              marker: tipSettings.marker.color,
              highlighter: tipSettings.highlighter.color,
            }}
            brushWidth={brushWidth}
            brushOpacity={brushOpacity}
            labels={{
              marker: t("canvas.sketch.penMarker"),
              highlighter: t("canvas.sketch.penHighlighter"),
              weight: t("canvas.sketch.weight"),
              transparency: t("canvas.sketch.transparency"),
              color: t("canvas.sketch.penColor"),
            }}
            onPenKindChange={applyPenKind}
            onColorChange={(color) => patchTipSettings({ color })}
            onWidthChange={(width) => patchTipSettings({ width })}
            onOpacityChange={(opacity) => patchTipSettings({ opacity })}
            onClose={() => setPenFlyoutOpen(false)}
            anchorRef={penButtonRef}
          />
        </div>

        {videoMode ? null : (
          <>
            <EditToolbarButton
              title={t("canvas.sketch.toolZoomIn")}
              disabled={camera.zoom >= SKETCH_VIEWPORT_MAX_ZOOM}
              onClick={() => {
                closeFlyouts()
                nudgeCameraZoom(SKETCH_ZOOM_STEP)
              }}
            >
              <ZoomIn className="h-4 w-4" />
            </EditToolbarButton>
            <EditToolbarButton
              title={t("canvas.sketch.toolZoomOut")}
              disabled={camera.zoom <= SKETCH_VIEWPORT_MIN_ZOOM}
              onClick={() => {
                closeFlyouts()
                nudgeCameraZoom(1 / SKETCH_ZOOM_STEP)
              }}
            >
              <ZoomOut className="h-4 w-4" />
            </EditToolbarButton>
            <EditToolbarButton
              active={tool === "pan"}
              title={t("canvas.sketch.toolPan")}
              onClick={() => {
                setTool("pan")
                closeFlyouts()
              }}
            >
              <Hand className="h-4 w-4" />
            </EditToolbarButton>
          </>
        )}

        {showEraser ? (
          <div className="relative">
            <EditToolbarButton
              ref={eraserButtonRef}
              active={tool === "eraser"}
              title={t("canvas.sketch.toolEraser")}
              onClick={pickEraser}
            >
              <Eraser className="h-4 w-4" />
            </EditToolbarButton>
            {eraserFlyout}
          </div>
        ) : null}

        {showShape ? (
          <div className="relative">
            <EditToolbarButton
              ref={shapeButtonRef}
              active={tool === "shape"}
              title={t("canvas.sketch.toolObjects")}
              onClick={pickShape}
            >
              {shapeKind === "ellipse" ? <Circle className="h-4 w-4" /> : <Square className="h-4 w-4" />}
            </EditToolbarButton>
            {shapeFlyout}
          </div>
        ) : null}

        {showArrow ? (
          <div className="relative">
            <EditToolbarButton
              ref={arrowButtonRef}
              active={tool === "arrow"}
              title={t("canvas.sketch.toolArrow")}
              onClick={pickArrow}
            >
              <ArrowUpRight className="h-4 w-4" />
            </EditToolbarButton>
            {arrowFlyout}
          </div>
        ) : null}

        {showArrowLabel ? (
          <div className="relative">
            <EditToolbarButton
              ref={arrowLabelButtonRef}
              active={tool === "arrowLabel"}
              title={t("canvas.sketch.toolArrowLabel")}
              onClick={pickArrowLabel}
            >
              <span className="relative inline-flex h-4 w-4 items-center justify-center">
                <ArrowUpRight className="absolute top-0 left-0 h-3.5 w-3.5" />
                <Type className="absolute right-0 bottom-0 h-2 w-2" />
              </span>
            </EditToolbarButton>
            {arrowLabelFlyout}
          </div>
        ) : null}

        {showText ? (
          <EditToolbarButton active={tool === "text"} title={t("canvas.sketch.toolText")} onClick={pickText}>
            <Type className="h-4 w-4" />
          </EditToolbarButton>
        ) : null}

        {showHistory ? (
          <>
            <div className="mx-1 h-5 w-px bg-ui-border" />
            {showUndo ? (
              <EditToolbarButton title={t("canvas.sketch.undo")} disabled={history.length === 0} onClick={undo}>
                <Undo2 className="h-4 w-4" />
              </EditToolbarButton>
            ) : null}
            {showRedo ? (
              <EditToolbarButton title={t("canvas.sketch.redo")} disabled={future.length === 0} onClick={redo}>
                <Redo2 className="h-4 w-4" />
              </EditToolbarButton>
            ) : null}
          </>
        ) : null}

        {effectiveShowAiPanel && !isDesktop ? (
          <div className="relative">
            <span title={aiToolbarTitle} className="inline-flex">
              <EditToolbarButton
                ref={aiButtonRef}
                active={sketchAvailable && aiFlyoutOpen}
                title={aiToolbarTitle}
                disabled={!sketchAvailable}
                onClick={() => {
                  if (!sketchAvailable) return
                  setPenFlyoutOpen(false)
                  setEraserFlyoutOpen(false)
                  setShapeFlyoutOpen(false)
                  setArrowFlyoutOpen(false)
                  setMoreMenuOpen(false)
                  const opening = !aiFlyoutOpen
                  if (opening) {
                    setSelectedIds([])
                    setEditingTextId(null)
                    setEditingArrowId(null)
                  }
                  setAiFlyoutOpen(opening)
                }}
              >
                <Sparkles className="h-4 w-4" />
              </EditToolbarButton>
            </span>
            <SketchAiFlyout
              open={aiFlyoutOpen}
              {...aiFormProps}
              onClose={() => setAiFlyoutOpen(false)}
              anchorRef={aiButtonRef}
            />
          </div>
        ) : null}

        {!formatOverflow && !videoMode ? (
          <>
            <div className="mx-1 h-5 w-px bg-ui-border" />
            {formatDropdown}
          </>
        ) : null}
        {editShowResolution && sourceNatural && !videoMode ? (
          <span className="shrink-0 px-1 text-xs text-ui-text-secondary tabular-nums">
            {sourceNatural.width}&times;{sourceNatural.height}
          </span>
        ) : null}
        {showDownload && !videoMode ? (
          <EditToolbarButton
            title={t("imageLightbox.downloadOriginal", { ns: "common" })}
            disabled={confirmBusy}
            onClick={() => void handleDownloadOriginal()}
          >
            <Download className="h-4 w-4" />
          </EditToolbarButton>
        ) : null}
        {showCopy && !videoMode ? (
          <EditToolbarButton
            title={t("imageLightbox.copyTitle", { ns: "common" })}
            disabled={confirmBusy || copying || !baseUrl}
            onClick={() => void handleViewCopy()}
          >
            {copying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
          </EditToolbarButton>
        ) : null}

        {(moreItems.length > 0 || formatOverflow) && !videoMode ? (
          <EditToolbarMore
            open={moreMenuOpen}
            onOpenChange={(next) => {
              if (next) closeFlyouts()
              setMoreMenuOpen(next)
            }}
            active={overflowToolActive}
            menuAria={t("canvas.sketch.viewActionsMenu")}
            moreButtonRef={moreButtonRef}
            items={moreItems}
            header={formatOverflow ? formatDropdown : undefined}
          >
            {showEraser ? null : eraserFlyout}
            {showShape ? null : shapeFlyout}
            {showArrow ? null : arrowFlyout}
            {showArrowLabel ? null : arrowLabelFlyout}
          </EditToolbarMore>
        ) : null}
      </div>
    </div>
  )

  const zoomOverlay = useMemo(() => {
    const oneToOne = sourceOneToOneZoom(sourceNatural, size)
    const preset = matchingZoomPreset(camera.zoom, oneToOne)
    const percentLabel = (percent: number) => t("canvas.sketch.zoomPercent", { percent })
    const fitLabel = t("canvas.sketch.zoomFit")
    const label =
      preset === "fit"
        ? fitLabel
        : preset === "100"
          ? percentLabel(100)
          : preset === "200"
            ? percentLabel(200)
            : percentLabel(displayZoomPercent(camera.zoom, oneToOne))
    return {
      value: preset ?? "",
      label,
      fitLabel,
      percent100Label: percentLabel(100),
      percent200Label: percentLabel(200),
      menuAria: t("canvas.sketch.zoomMenuAria"),
    }
  }, [camera.zoom, sourceNatural, size, t])

  const viewHeaderActions = showHeaderActions ? (
    <SketchViewHeaderActions
      maxWidth={viewHeaderActionsMaxWidth}
      resolution={sourceNatural ? { w: sourceNatural.width, h: sourceNatural.height } : null}
      copying={copying}
      showEdit={showEditEntry}
      showCopy={!mediaViewMode}
      showSave={videoCutDirty}
      saveBusy={videoCutSaving}
      onDownload={handleViewDownload}
      onCopy={() => void handleViewCopy()}
      onEdit={() => {
        if (!allowEdit || mediaViewMode) return
        setViewMode(false)
      }}
      onSave={() => void handleSaveVideoCut()}
      labels={{
        download: t("imageLightbox.downloadOriginal", { ns: "common" }),
        copy: t("imageLightbox.copy", { ns: "common" }),
        copyTitle: t("imageLightbox.copyTitle", { ns: "common" }),
        edit: t("canvas.sketch.startEditing"),
        save: t("save", { ns: "common" }),
        menuAria: t("canvas.sketch.viewActionsMenu"),
      }}
    />
  ) : null

  const editCanvasBody = (
    <>
      {baseImageLoading ? (
        <AssetPreviewBusySpinner />
      ) : (
        <div
          className={cn("relative shrink-0 overflow-clip", aiBusy && "tile-gen-shimmer")}
          aria-busy={aiBusy}
          style={{ width: size.width, height: size.height }}
        >
          {canvasReady ? (
            <SketchCanvas
              width={size.width}
              height={size.height}
              baseUrl={baseUrl}
              baseImage={baseImage}
              background={baseUrl ? "none" : "white"}
              strokes={strokes}
              texts={texts}
              arrows={arrows}
              shapes={shapes}
              tool={tool}
              color={penColor}
              brushWidth={brushWidth}
              brushOpacity={brushOpacity}
              eraserWidth={eraserWidth}
              arrowColor={arrowColor}
              arrowWidth={arrowWidth}
              arrowLabelColor={arrowLabelColor}
              arrowLabelWidth={arrowLabelWidth}
              shapeKind={shapeKind}
              shapeColor={shapeColor}
              shapeWidth={shapeWidth}
              textColor={textColor}
              penKind={penKind}
              selectedIds={selectedIds}
              editingTextId={editingTextId}
              editingArrowId={editingArrowId}
              disabled={aiBusy || confirmBusy}
              deleteLabel={t("canvas.sketch.delete")}
              textStyleLabels={{
                style: t("canvas.sketch.textStyle"),
                size: t("canvas.sketch.textSize"),
                fill: t("canvas.sketch.textFill"),
              }}
              onDeleteSelected={deleteSelected}
              onStrokeStart={() => {
                pushHistory()
                closeFlyouts()
              }}
              onStrokesChange={setStrokes}
              onTextsChange={setTexts}
              onArrowsChange={setArrows}
              onShapesChange={setShapes}
              onSelectIds={setSelectedIds}
              onEditingTextChange={setEditingTextId}
              onEditingArrowChange={setEditingArrowId}
              onToolChange={setTool}
              onObjectMutateStart={pushHistory}
              onTextColorChange={setTextColor}
              camera={camera}
              onCameraChange={setCamera}
              onZoomPreset={applyZoomPreset}
              zoomEnabled={!videoMode}
              zoomOverlay={zoomOverlay}
            />
          ) : null}
          {confirmBusy ? (
            <>
              <AssetPreviewBusyDim />
              <AssetPreviewBusySpinner />
            </>
          ) : null}
        </div>
      )}
    </>
  )

  return (
    <>
      <WorkspaceDialog
        open={open}
        onOpenChange={(next) => void requestClose(next)}
        title={
          viewMode || lockHeaderTitle ? (
            <span className="block w-full truncate text-base font-semibold text-ui-text-primary" title={headerLabel}>
              {headerLabel}
            </span>
          ) : (
            <span className="relative block min-w-0">
              <span className="invisible block truncate text-base font-semibold" aria-hidden>
                {headerLabel}
              </span>
              {editingTitle ? (
                <input
                  ref={titleInputRef}
                  value={titleDraft}
                  disabled={aiBusy || confirmBusy}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={commitTitleEdit}
                  onKeyDown={(e) => {
                    e.stopPropagation()
                    if (e.key === "Enter") {
                      e.preventDefault()
                      commitTitleEdit()
                    }
                    if (e.key === "Escape") {
                      e.preventDefault()
                      setTitleDraft(fileName)
                      setEditingTitle(false)
                    }
                  }}
                  className="absolute inset-0 w-full min-w-0 rounded-md border border-ui-accent bg-ui-surface-sunken px-2 py-0.5 text-base font-semibold text-ui-text-primary outline-none"
                  size={1}
                  aria-label={t("canvas.sketch.renameTitleHint")}
                />
              ) : (
                <button
                  type="button"
                  title={fileName || t("canvas.sketch.renameTitleHint")}
                  disabled={aiBusy || confirmBusy}
                  onClick={() => {
                    setTitleDraft(fileName)
                    setEditingTitle(true)
                  }}
                  className="absolute inset-0 w-full truncate rounded-md text-left text-base font-semibold text-ui-text-primary hover:underline disabled:opacity-50"
                >
                  {headerLabel}
                </button>
              )}
            </span>
          )
        }
        headerCenter={viewMode ? undefined : toolbar}
        headerActions={viewHeaderActions}
        titleWrapClassName={
          viewMode ? "min-w-0 flex-1" : "min-w-0 w-[min(6rem,14vw)] shrink-0 overflow-hidden sm:w-[9rem]"
        }
        closeAriaLabel={t("close", { ns: "common" })}
        maxHeightClassName="max-h-[95vh]"
        contentClassName={
          videoMode
            ? "min-h-0 max-w-[min(96vw,1612px)] overflow-hidden"
            : viewMode || flushEditWell
              ? "min-h-0 max-w-[min(96vw,1280px)] overflow-hidden"
              : "min-h-0 w-[min(96vw,1280px)] overflow-hidden"
        }
        contentStyle={
          videoMode
            ? { width: videoDialogWidth }
            : viewMode
              ? { width: viewDialogWidth }
              : flushEditWell
                ? { width: editDialogWidth }
                : undefined
        }
        preventDismiss={confirmBusy || video.submitting}
        nested={nested}
        onEscapeKeyDown={(e) => {
          if (editingTextId || editingArrowId || editingTitle) {
            e.preventDefault()
          }
        }}
        onOpenAutoFocus={(e) => {
          if (videoMode) {
            // Не фокусировать кнопку Start (Space активировал бы её) — сразу в промпт.
            e.preventDefault()
            requestAnimationFrame(() => videoPromptEditorRef.current?.focus())
            return
          }
          if (videoViewMode) {
            // Фокус на плеер, а не на Close: Space не должен активировать кнопку закрытия.
            e.preventDefault()
            requestAnimationFrame(() => paneRef.current?.focus({ preventScroll: true }))
            return
          }
          if (audioViewMode) {
            e.preventDefault()
            requestAnimationFrame(() => viewAudioRef.current?.focus({ preventScroll: true }))
          }
        }}
        bodyClassName={cn(
          "flex min-h-0 flex-col",
          videoMode
            ? "flex-1 overflow-hidden p-3"
            : viewMode || flushEditWell
              ? "flex-none overflow-hidden p-0"
              : "flex-1 overflow-hidden p-3",
        )}
        footer={
          videoMode ? undefined : showFooter ? (
            <>
              {showLibraryPersist ? (
                <Button
                  type="button"
                  variant={showConfirmPersist ? "outline" : "default"}
                  disabled={librarySaveDisabled}
                  onClick={() => void handleSaveToLibrary()}
                >
                  {t("canvas.sketch.confirmSaveLibrary")}
                </Button>
              ) : null}
              {showConfirmPersist ? (
                <Button type="button" disabled={aiBusy || confirmBusy} onClick={() => void handleConfirm()}>
                  {primaryConfirmLabel}
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      >
        {videoMode && videoStageConfig ? (
          <div className="flex min-h-0 w-full min-w-0 flex-1 flex-row items-stretch gap-3">
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <SketchVideoCorrectionStage
                open={open}
                config={videoStageConfig}
                onPlayingChange={video.setPlaying}
                onMediaReady={video.onMediaReady}
                onPlayheadSettled={video.onPlayheadSettled}
                onPlayheadInteractStart={dismissLiveInk}
                overlayRef={video.overlayRef}
                playbackApiRef={video.playbackApiRef}
                shellFloatingAction={
                  videoHasInk && !video.playing && video.mediaReady ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="shadow-md"
                      disabled={!video.canAddMarkup || video.submitting || video.exporting}
                      onClick={() => void handleSaveVideoMarkup()}
                    >
                      {video.exporting ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
                      {t("correction.addMarkup", { ns: "shot" })}
                    </Button>
                  ) : null
                }
                sketchOverlay={
                  !video.playing && video.mediaReady ? (
                    <SketchCanvas
                      width={video.stageSize.width}
                      height={video.stageSize.height}
                      baseImage={null}
                      background="none"
                      strokes={strokes}
                      texts={texts}
                      arrows={arrows}
                      shapes={shapes}
                      tool={tool}
                      color={penColor}
                      brushWidth={brushWidth}
                      brushOpacity={brushOpacity}
                      eraserWidth={eraserWidth}
                      arrowColor={arrowColor}
                      arrowWidth={arrowWidth}
                      arrowLabelColor={arrowLabelColor}
                      arrowLabelWidth={arrowLabelWidth}
                      shapeKind={shapeKind}
                      shapeColor={shapeColor}
                      shapeWidth={shapeWidth}
                      textColor={textColor}
                      penKind={penKind}
                      selectedIds={selectedIds}
                      editingTextId={editingTextId}
                      editingArrowId={editingArrowId}
                      disabled={video.playing || video.submitting || video.exporting || !video.mediaReady}
                      deleteLabel={t("canvas.sketch.delete")}
                      textStyleLabels={{
                        style: t("canvas.sketch.textStyle"),
                        size: t("canvas.sketch.textSize"),
                        fill: t("canvas.sketch.textFill"),
                      }}
                      onDeleteSelected={deleteSelected}
                      onStrokeStart={() => {
                        pushHistory()
                        closeFlyouts()
                      }}
                      onStrokesChange={setStrokes}
                      onTextsChange={setTexts}
                      onArrowsChange={setArrows}
                      onShapesChange={setShapes}
                      onSelectIds={setSelectedIds}
                      onEditingTextChange={setEditingTextId}
                      onEditingArrowChange={setEditingArrowId}
                      onToolChange={setTool}
                      onObjectMutateStart={pushHistory}
                      onTextColorChange={setTextColor}
                      camera={FIT_CAMERA}
                      onCameraChange={() => {}}
                      onZoomPreset={() => {}}
                      zoomEnabled={false}
                      zoomOverlay={zoomOverlay}
                    />
                  ) : null
                }
              />
            </div>

            <div className="flex min-h-0 shrink-0 flex-col gap-2" style={{ width: VIDEO_SIDEBAR_W }}>
              {video.markups.length > 0 ? (
                <div className="flex shrink-0 flex-col gap-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-medium text-ui-text-secondary">
                      {t("correction.markups", { ns: "shot" })}
                    </div>
                    <div className="text-[11px] text-ui-text-muted">
                      {t("correction.markupCap", {
                        ns: "shot",
                        count: video.markups.length,
                        max: videoStageConfig.markupMax,
                      })}
                    </div>
                  </div>
                  <ReferenceStrip
                    layout="wrap"
                    showAddTile={false}
                    projectId={projectId}
                    references={video.markups.map((m) => {
                      const tc = formatTimecode(m.timecodeMs, videoFps)
                      return {
                        key: m.id,
                        url: m.previewUrl,
                        thumbUrl: m.previewUrl,
                        downloadUrl: m.previewUrl,
                        label: `@${tc}`,
                        mediaType: "image" as const,
                      }
                    })}
                    onRemove={(index) => {
                      if (video.submitting) return
                      const m = video.markups[index]
                      if (m) video.removeMarkup(m.id)
                    }}
                    onInsertMention={(label) => videoPromptEditorRef.current?.insertReference(label)}
                    onOpenPreview={(index) => {
                      const m = video.markups[index]
                      if (!m) return
                      video.seekToMarkup(m.timecodeMs)
                    }}
                  />
                </div>
              ) : null}
              <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
                <PromptEditor
                  ref={videoPromptEditorRef}
                  valueText={video.correctionPrompt}
                  seedKey={open ? `video-correction-${videoStageConfig.correctionClipId}` : "closed"}
                  mentionItems={videoMarkupMentionItems}
                  onChange={(text) => video.setCorrectionPrompt(text)}
                  placeholder={t("placeholders.correctionVideo", { ns: "shot" })}
                  ariaLabel={t("placeholders.correctionVideo", { ns: "shot" })}
                  showSegmentButton={false}
                  showMentionHint
                  mentionHintText={t("correction.markupMentionHint", { ns: "shot" })}
                  editable={!video.submitting}
                  fillAvailable
                  className="h-full min-h-0 focus-within:!shadow-none"
                  contentClassName="!h-full !min-h-0 !max-h-none resize-none !pb-10"
                />
                <div className="absolute right-1 bottom-1 z-10">
                  <Button
                    type="button"
                    size="sm"
                    className="shadow-lg"
                    disabled={!video.correctionPrompt.trim() || video.submitting}
                    onClick={() => void video.submitCorrection()}
                  >
                    {t("correction.startCorrection", { ns: "shot" })}
                  </Button>
                </div>
              </div>
              {video.error ? <p className="shrink-0 text-sm text-ui-danger">{video.error}</p> : null}
            </div>
          </div>
        ) : viewMode ? (
          videoViewMode && videoUrl ? (
            <div
              ref={paneRef}
              tabIndex={-1}
              className="w-full max-w-full outline-none focus:outline-none focus-visible:outline-none"
              style={{ width: videoViewWidth > 0 ? videoViewWidth : "min(96vw, 960px)" }}
            >
              <SingleClipPlayer
                key={videoUrl}
                sourceId={1}
                streamUrl={videoUrl}
                durationMs={videoDurationMs}
                aspectRatio={sourceAspect}
                active={open}
                autoPlay={false}
                showTrackHeaders={false}
                capabilities={onSaveVideoCut ? { resize: true } : undefined}
                onDocChange={onSaveVideoCut ? handleVideoDocChange : undefined}
                onMediaSize={({ width, height }) => {
                  setSourceAspect(width / height)
                  setSourceNatural({ width, height })
                }}
                className="w-full"
              />
            </div>
          ) : audioViewMode && audioUrl ? (
            <div ref={paneRef} className="flex w-full items-center justify-center p-6">
              <audio
                ref={viewAudioRef}
                key={audioUrl}
                src={audioUrl}
                controls
                autoPlay
                tabIndex={0}
                className="w-full max-w-xl outline-none focus:outline-none focus-visible:outline-none"
              />
            </div>
          ) : baseUrl && viewSizeReady && !baseImageLoading ? (
            <div
              ref={paneRef}
              className="shrink-0 overflow-hidden"
              style={{ width: viewPreviewSize.width, height: viewPreviewSize.height }}
            >
              <img src={baseUrl} alt={fileName} className="block h-full w-full" />
            </div>
          ) : baseUrl ? (
            <div
              ref={paneRef}
              className="relative flex items-center justify-center overflow-hidden"
              style={{ width: viewPreviewSize.width, height: viewPreviewSize.height }}
            >
              <AssetPreviewBusySpinner />
            </div>
          ) : null
        ) : isNewSketch ? (
          <div
            className="flex min-h-[min(42vh,380px)] w-full min-w-0 flex-col gap-2"
            style={{ height: editMaxH + editStagePadY }}
          >
            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
              <div className="relative min-h-0 w-full flex-1 basis-0 bg-ui-surface-sunken dark:bg-zinc-800">
                <div className="absolute inset-0 p-3">
                  <div
                    ref={paneRef}
                    className="relative flex h-full w-full items-center justify-center overflow-hidden"
                  >
                    {editCanvasBody}
                  </div>
                </div>
                {effectiveShowAiPanel && isDesktop ? (
                  <SketchAiFloatingCard {...aiFormProps} resetKey={`${open}:${sourceUrl ?? "blank"}`} />
                ) : null}
              </div>
            </div>
          </div>
        ) : (
          <div
            ref={paneRef}
            className="relative shrink-0 overflow-hidden"
            style={{ width: editPreviewSize.width, height: editPreviewSize.height }}
          >
            {editCanvasBody}
            {effectiveShowAiPanel && isDesktop ? (
              <SketchAiFloatingCard {...aiFormProps} resetKey={`${open}:${sourceUrl ?? "blank"}`} />
            ) : null}
          </div>
        )}
      </WorkspaceDialog>
    </>
  )
}
